/**
 * Company Formation & Governance — 33 documents.
 *
 * Entity type is load-bearing in this category: an LLC uses an operating agreement, membership
 * interests and member consents; a corporation uses bylaws, stock, shareholder agreements and
 * board consents. None of these entries carry a template — formation and equity documents are
 * entity- and state-specific and belong with counsel.
 */
export const FORMATION_DOCS = [
  {
    id: "founder-agreement",
    name: "Founder Agreement",
    category: "formation",
    subcategory: "Founders & ownership",
    aliases: ["Founders Agreement", "Founder Terms", "Startup Founder Agreement"],
    stages: ["idea", "development"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Founders + Company (or founders with each other, before the entity exists)",
    whenYouNeedIt:
      "As soon as more than one person is working on the brand — usually before the entity exists, while you are still making test batches and taking co-packer calls.",
    whatItDoes:
      "Records what each founder contributes, what each one owns, who decides what, and what happens to that ownership if someone stops showing up.",
    whyItMatters:
      "In a CPG brand the founding work is unusually lopsided: one person develops the formula, one works the accounts, one funds the first production run off a personal card. If none of that is written down, every contribution gets re-argued the first time real money appears — a distributor deal, a chain authorization, an acquisition offer. Investors and buyers read this document before they read your P&L, and its absence is priced in.",
    majorTerms: [
      "Equity split between founders",
      "Vesting schedule and cliff for each founder",
      "Roles, titles and decision authority",
      "Time commitment and outside activities",
      "Capital contributions and how future cash is funded",
      "Pre-formation IP being contributed (recipes, brand name, artwork, photography)",
      "Confidentiality between founders",
      "What happens to equity on departure",
      "Signing authority before the entity exists",
      "How disagreements get resolved",
    ],
    clausesToWatch: [
      {
        clause: "Contributed intellectual property",
        why: "If the recipe and brand name developed before the entity are not assigned in writing, the company is selling a product it does not own.",
      },
      {
        clause: "Vesting and departure",
        why: "Without vesting, a founder who leaves in month five keeps a full stake and permanently dilutes everyone who stays.",
      },
      {
        clause: "Decision authority and signing limits",
        why: "If nobody is clearly authorised to commit, the co-packer's production slot is gone before the founders finish arguing about it.",
      },
      {
        clause: "Future funding obligations",
        why: "Undefined funding leads to one founder quietly paying for inventory and later claiming a larger share for it.",
      },
      {
        clause: "Time commitment",
        why: "A founder who keeps a full-time job while holding an equal share is the first thing an investor questions and the hardest thing to renegotiate later.",
      },
      {
        clause: "Post-departure restrictions and brand use",
        why: "Silence lets a departing founder launch a near-identical product in the same category using everything they learned.",
      },
    ],
    cpgIssues: [
      "The recipe developed in a founder's home kitchen belongs to that founder until it is assigned in writing — a co-packer will produce it either way and never ask.",
      "The brand name, domain, Shopify store, Amazon Seller Central login and Instagram handle usually sit in one founder's personal accounts and never move to the company.",
      "CPG is working-capital heavy: the next production run needs cash months before the revenue lands, so who funds it and on what terms is a founder question, not an accounting question.",
      "Trademark applications filed in an individual's name before the entity existed surface in every diligence review and take time to clean up.",
      "The founder who is the face of the brand — on pack, at demos, on social — creates a name and likeness question the day they leave.",
    ],
    founderMistakes: [
      "Splitting equally by reflex among three friends, then finding at month nine that two had stopped working and still held two thirds of the company.",
      "Never assigning the recipe, and learning at the term sheet that the co-founder who left still owns it.",
      "Funding the first production run on a personal card with no note and no equity paperwork, leaving the contribution invisible on the cap table.",
      "Giving a co-founder title to the person who made the co-packer introduction, with no vesting and no defined role.",
    ],
    negotiationPoints: [
      "Ask for vesting on every founder stake, including your own, with a cliff.",
      "Ask that every recipe, formulation note, brand name, logo file and product photograph created before formation is assigned to the company by name, on a schedule.",
      "Ask for a written definition of full-time and a stated consequence if someone goes part-time.",
      "Agree now how the next production run gets funded and what happens if a founder cannot contribute.",
      "Set who may sign a purchase order or a co-packer commitment alone, and the value above which it needs everyone.",
    ],
    redFlags: [
      "A founder who will not assign pre-formation recipes, artwork or the brand name to the company.",
      "No vesting on any stake.",
      "Equity granted for a single introduction with no ongoing work.",
      "We will paper it later, while production is already scheduled and deposits are paid.",
      "A founder who will not disclose whether an existing employer's IP agreement could reach this work.",
    ],
    beforeYouSign: [
      "A written list of every asset the venture uses and whose name it currently sits in: trademark filings, domain, social handles, seller accounts, formula documents, artwork files.",
      "Each founder's honest time commitment and outside obligations, in writing.",
      "A first-year cash plan showing what the next production run costs and who funds it.",
    ],
    related: [
      "co-founder-agreement",
      "founder-vesting-agreement",
      "founder-ip-assignment",
      "llc-operating-agreement",
      "corporate-bylaws",
      "piia",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "co-founder-agreement",
    name: "Co-Founder Agreement",
    category: "formation",
    subcategory: "Founders & ownership",
    aliases: ["Co-Founders Agreement", "Partner Agreement", "Founder Operating Terms"],
    stages: ["idea", "development", "pre-launch"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Co-Founders + Company",
    whenYouNeedIt:
      "When two or more people are running the brand together, and again when someone joins early enough to be called a co-founder rather than an employee.",
    whatItDoes:
      "Sets how the founders work together day to day: who owns which function, which decisions need agreement, how someone exits, and what the leaver keeps.",
    whyItMatters:
      "A co-founder who stops working while holding a quarter of the company is the most common reason a promising CPG brand becomes unfundable. The damage is not the equity alone — it is that the sales founder holds the buyer relationships and the operations founder holds the co-packer and the formula, so a split can strand both sides of the business at once. Buyers and investors ask for this document early and read the departure terms first.",
    majorTerms: [
      "Ownership split and any performance conditions on it",
      "Functional ownership: sales, operations, product, finance",
      "Decisions requiring unanimous agreement",
      "Compensation and when salaries start",
      "Vesting and repurchase on departure",
      "Confidentiality and post-departure conduct",
      "Deadlock procedure",
      "Transfer restrictions on founder equity",
      "Personal guarantees and who is exposed",
      "How a founder can be removed from a role",
    ],
    clausesToWatch: [
      {
        clause: "Reserved decisions",
        why: "If everything needs unanimity you deadlock; if nothing does, one founder can sign a multi-year distribution deal the others never saw.",
      },
      {
        clause: "Removal from a role versus removal of equity",
        why: "Founders confuse the two, then discover a co-founder can be taken out of the operations seat while keeping every share.",
      },
      {
        clause: "Deadlock resolution",
        why: "A stalemate during a booked production run costs the slot, the ingredients and often the retailer's reset window.",
      },
      {
        clause: "Departure and the accounts a founder holds",
        why: "Without an obligation to hand over logins, buyer contacts and the co-packer relationship, a departure takes the business relationships with it.",
      },
      {
        clause: "Personal guarantees and reimbursement",
        why: "One founder often guarantees the co-packer or equipment lease personally, and nothing says what the others owe if it is called.",
      },
      {
        clause: "Non-compete and non-solicit between founders",
        why: "A departing founder who launches a competing SKU with the same co-packer is a commercial problem long before it is a legal one.",
      },
    ],
    cpgIssues: [
      "Function split maps to relationships: the sales founder holds the distributor and buyer contacts, the operations founder holds the co-packer, the flavor house and the specification file. A split without a handover clause breaks whichever side leaves.",
      "Production slots are booked months ahead and ingredient and packaging minimums are large — a founder disagreement that stalls a decision by three weeks can push a launch a full quarter.",
      "Personal guarantees are routine in early CPG: co-packers, ingredient suppliers, equipment lessors and landlords all ask, and usually only one founder signs.",
      "The formula, cost sheets and landed-cost model often live in one founder's personal drive; if they leave, the company may not have its own product documentation.",
      "A co-founder who departs and starts a similar product in the same category can approach your co-packer, your broker and your buyer in a single week — the industry is small.",
    ],
    founderMistakes: [
      "Agreeing a 50/50 split with no tiebreak, then losing a retailer reset window while the two founders argued about pricing.",
      "Letting one founder hold every vendor login and the master formula file, and having no copy when they left.",
      "Never documenting that one founder personally guaranteed the co-packer credit line, so nothing happened when the company could not pay.",
      "Bringing on a third co-founder for a distributor introduction, at full co-founder equity, with no vesting.",
    ],
    negotiationPoints: [
      "Ask for a short list of reserved decisions — new debt, personal guarantees, exclusivity, equity issuance, selling the company — and majority rule on everything else.",
      "Ask for a handover obligation on departure covering logins, contacts, the formula file and the cost model, with a deadline.",
      "Ask that any personal guarantee is disclosed to all founders and reimbursed by the company or shared.",
      "Separate role removal from equity forfeiture, and write both down.",
      "Set a deadlock path that ends in a decision, not in silence, with a timeframe short enough to protect a production slot.",
    ],
    redFlags: [
      "No tiebreak in a two-founder company.",
      "Equity with no vesting for a co-founder who joined after the product existed.",
      "A founder unwilling to put company files and vendor accounts under company control.",
      "No restriction on a departing founder using the same co-packer for a competing product.",
      "Silence on who is responsible when a personal guarantee gets called.",
    ],
    beforeYouSign: [
      "An inventory of every vendor account, login, contract and file, and whose personal name or email it is under.",
      "A list of every personal guarantee already signed and by whom.",
      "An honest description of what each founder does full time and what they are giving up to do it.",
    ],
    related: [
      "founder-agreement",
      "founder-vesting-agreement",
      "founder-ip-assignment",
      "buy-sell-agreement",
      "shareholder-agreement",
      "multi-member-llc-operating-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "llc-operating-agreement",
    name: "LLC Operating Agreement",
    category: "formation",
    subcategory: "LLC governance",
    aliases: ["Operating Agreement", "LLC Agreement", "Company Agreement"],
    stages: ["idea", "pre-launch", "launch"],
    importance: "essential",
    entityTypes: ["llc"],
    parties: "LLC + its Members",
    whenYouNeedIt:
      "Immediately after forming an LLC, and before the company signs anything significant — a co-packer agreement, a lease, a distributor deal or a bank account application.",
    whatItDoes:
      "Sets out who owns the LLC, how profits and losses are allocated, who manages it, who can sign, how new money comes in, and what happens when a member leaves.",
    whyItMatters:
      "Filing with the state creates the LLC. It does not record who owns what, who may commit the company, how cash gets distributed, or what happens if a member walks away — without this document your LLC runs on whatever default rules your state supplies, and those defaults were not written around a business that buys inventory nine months before it gets paid. Banks, 3PLs, retailers and buyers all ask for it, and the version you write in a hurry during diligence is never as good as the one you write now.",
    majorTerms: [
      "Membership interests or units and the ownership schedule",
      "Initial capital contributions",
      "Additional capital calls and the consequence of not funding one",
      "Allocation of profits and losses",
      "Distributions and the reserve kept for inventory",
      "Member-managed or manager-managed structure",
      "Voting thresholds and reserved matters",
      "Officer titles and signing authority",
      "Transfer restrictions and admission of new members",
      "Withdrawal, death, disability and buy-out mechanics",
      "Books, records and member information rights",
      "Dissolution and winding up",
    ],
    clausesToWatch: [
      {
        clause: "Signing authority and spending limits",
        why: "With no limit, one member can bind the company to a minimum-volume co-packer commitment or a multi-year distribution deal alone.",
      },
      {
        clause: "Capital calls and dilution",
        why: "If the agreement is silent, funding the next production run becomes an argument rather than a procedure, usually at the worst possible moment.",
      },
      {
        clause: "Distributions",
        why: "A mandatory distribution provision can pull out the cash you needed for the next run of cans, which have long lead times and large minimums.",
      },
      {
        clause: "Transfer restrictions",
        why: "Without them a member can sell their interest to a competitor, a distributor or a stranger and you inherit a new partner.",
      },
      {
        clause: "Buy-out on departure and how the price is set",
        why: "An undefined valuation method in an inventory-heavy business turns every exit into a fight over what the brand is worth.",
      },
      {
        clause: "Reserved matters requiring unanimity",
        why: "Too broad and you deadlock during a production window; too narrow and major commitments happen without you.",
      },
    ],
    cpgIssues: [
      "Capital calls in CPG are triggered by production, not by strategy: a can or label minimum, a co-packer deposit or a retailer authorization all demand cash before revenue exists.",
      "The legal entity name appears on the label in the responsible-party line, so changing entity or name later means new artwork and a reprint against packaging minimums.",
      "Facility registrations, state licenses and certifications such as organic or kosher are issued to a named legal entity — moving to a different entity means redoing them.",
      "Your GS1 company prefix and UPCs are issued to a specific legal entity; retailers match that name against the vendor record and the insurance certificate.",
      "Holding inventory at a 3PL in another state can create registration and tax obligations in that state, which is a question for your accountant and counsel before the pallets ship.",
      "Co-packers, ingredient suppliers and equipment lessors routinely ask a member to guarantee personally, which sits outside the LLC entirely.",
    ],
    founderMistakes: [
      "Downloading a generic operating agreement and never completing the ownership schedule, leaving the company with no record of who owns what.",
      "Treating the state filing as the whole job, then failing a retailer vendor onboarding that required the agreement and a matching entity name.",
      "Distributing profit to members after a strong quarter and having nothing left to fund the next run when the retailer doubled the order.",
      "Letting one member sign a year-long ingredient commitment with no authority limit in the agreement.",
    ],
    negotiationPoints: [
      "Ask for a spending threshold above which any commitment needs more than one signature, set at a number that matters to your unit economics.",
      "Ask for a defined capital call procedure with a stated consequence, agreed while everyone is still friendly.",
      "Ask that distributions come after a working-capital reserve sized to your next production run.",
      "Ask for transfer restrictions that block a sale to a competitor, distributor or co-packer without consent.",
      "Ask for a buy-out valuation method written now, including who appraises and how quickly the company must pay.",
    ],
    redFlags: [
      "An ownership schedule left blank or inconsistent with what members believe they own.",
      "No signing authority or spending limits anywhere in the document.",
      "Mandatory distributions with no working-capital reserve.",
      "Unrestricted transfer of membership interests.",
      "No provision at all for a member's death, disability or exit.",
    ],
    beforeYouSign: [
      "A completed ownership schedule every member has read and agrees with.",
      "The entity name exactly as it will appear on labels, insurance certificates and retailer vendor records.",
      "A working-capital plan showing what the next production run and packaging order cost.",
      "A list of any personal guarantees members have already given.",
    ],
    related: [
      "single-member-llc-operating-agreement",
      "multi-member-llc-operating-agreement",
      "written-consent-of-members",
      "founder-ip-assignment",
      "buy-sell-agreement",
      "membership-interest-purchase-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "single-member-llc-operating-agreement",
    name: "Single-Member LLC Operating Agreement",
    category: "formation",
    subcategory: "LLC governance",
    aliases: ["Sole Member Operating Agreement", "One-Member LLC Agreement"],
    stages: ["idea", "pre-launch", "launch"],
    importance: "essential",
    entityTypes: ["llc"],
    parties: "LLC + its sole Member",
    whenYouNeedIt:
      "Right after forming a single-member LLC — usually the same week you open the business bank account and start talking to a co-packer.",
    whatItDoes:
      "States that the LLC is owned and managed by one member, sets how the member is paid and reimbursed, names who can act if the member cannot, and records the assets the member has put into the company.",
    whyItMatters:
      "Solo founders skip this because there is nobody to agree with, then hit it three ways: a bank or 3PL asks for it during onboarding, a retailer's vendor packet asks for it, and a buyer's counsel asks for it at exit. It is also the document that shows the brand, the recipe and the accounts belong to the company rather than to you personally — which is the difference between selling a business and selling a hobby. Whether the separation holds in a dispute is a question for counsel, but without the paperwork there is nothing to argue from.",
    majorTerms: [
      "Sole member ownership and the units held",
      "Management and the member's authority to bind the company",
      "Capital contributions, including assets contributed rather than cash",
      "Member compensation, draws and expense reimbursement",
      "Assets assigned into the company (recipe, brand name, domain, artwork)",
      "Successor manager if the member dies or is incapacitated",
      "Transfer of the membership interest",
      "Admission of a future second member",
      "Books, records and separate accounts",
      "Dissolution",
    ],
    clausesToWatch: [
      {
        clause: "Contributed assets schedule",
        why: "If the recipe, brand name and domain are not listed as contributed to the company, they are still personally yours and a buyer will say so.",
      },
      {
        clause: "Successor manager on death or incapacity",
        why: "With nobody named, an inventory position at a 3PL and a booked production run can sit frozen while the estate sorts itself out.",
      },
      {
        clause: "Member draws and reimbursement",
        why: "Undocumented movement of money between you and the company is the first thing a lender, a buyer or an auditor picks at.",
      },
      {
        clause: "Admission of a second member",
        why: "Without a defined path, bringing in a partner or investor later means rewriting the whole agreement under time pressure.",
      },
      {
        clause: "Authority to sign",
        why: "Contracts signed in your personal name rather than the company's undercut the separation the rest of the document is trying to create.",
      },
    ],
    cpgIssues: [
      "Shopify, Amazon Seller Central, the payment processor and the ad accounts are often opened in the founder's personal name and never transferred, so the company does not actually hold its own sales channels.",
      "Co-packers, ingredient brokers and equipment lessors will ask a solo founder for a personal guarantee, which sits outside the LLC no matter how the agreement is written.",
      "A trademark application filed personally before formation stays personal until it is assigned properly — list it in the contributed assets schedule and get the assignment handled.",
      "The label's responsible-party line and the insurance certificate must name the same legal entity the retailer has in its vendor record, or invoices and claims stall.",
      "If the sole founder is unavailable for a month, someone still has to release a production run, approve a label revision and answer a retailer chargeback.",
    ],
    founderMistakes: [
      "Skipping the agreement because it is just me, then failing a retailer or 3PL onboarding that required it.",
      "Running Shopify payouts into a personal account for a year and having no clean financial record when a buyer asked.",
      "Never assigning the trademark application from personal name to the company, and having to fix it under deal pressure.",
      "Naming nobody to act in an emergency while a full container of finished goods sat at a warehouse accruing storage fees.",
    ],
    negotiationPoints: [
      "Have the agreement list every contributed asset by name — recipe, formulation notes, brand name, trademark filings, domain, artwork files, photography, seller accounts.",
      "Name a successor manager who can act if you cannot, and tell that person they are named.",
      "Set a written reimbursement policy for expenses you pay personally, and follow it.",
      "Include a clear path to admit a second member so a future partner or investor does not require a rewrite.",
    ],
    redFlags: [
      "No contributed assets schedule, so the brand and formula remain personally held.",
      "No successor or emergency authority named anywhere.",
      "Company contracts being signed in the founder's personal name.",
      "A generic form with the wrong state or the wrong entity name left in it.",
    ],
    beforeYouSign: [
      "A list of every asset the brand runs on and whose name it is currently under.",
      "The exact legal entity name as it will appear on labels, the insurance certificate and retailer vendor records.",
      "A business bank account separate from every personal account.",
    ],
    related: [
      "llc-operating-agreement",
      "multi-member-llc-operating-agreement",
      "founder-ip-assignment",
      "trademark-assignment",
      "written-consent-of-members",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "multi-member-llc-operating-agreement",
    name: "Multi-Member LLC Operating Agreement",
    category: "formation",
    subcategory: "LLC governance",
    aliases: ["Member-Managed LLC Agreement", "Manager-Managed LLC Agreement", "Multi-Owner Operating Agreement"],
    stages: ["idea", "pre-launch", "growth"],
    importance: "essential",
    entityTypes: ["llc"],
    parties: "LLC + all Members (and the Manager, if manager-managed)",
    whenYouNeedIt:
      "Before a second member holds any interest — a co-founder, a family member who funded the first run, or an investor taking units instead of a note.",
    whatItDoes:
      "Defines each member's units, capital account, share of profit and loss, voting rights and exit terms, and separates the members who run the business from the members who only fund it.",
    whyItMatters:
      "Multi-member LLCs fail on two predictable points in CPG: someone cannot fund the capital call that pays for the next production run, and nobody agreed in advance what happens then. The second is a deadlock while a co-packer slot and a retailer reset window both expire. Written well, this document turns both into a procedure; written badly or not at all, they become a dispute that stops production while inventory ages.",
    majorTerms: [
      "Units held by each member and the capital account for each",
      "Capital call mechanics and dilution or penalty for not funding",
      "Profit and loss allocation versus cash distributions",
      "Working-capital reserve before any distribution",
      "Management structure and who is a manager",
      "Reserved matters and voting thresholds",
      "Vesting or forfeiture on units held by working members",
      "Transfer restrictions, right of first refusal and permitted transfers",
      "Buy-out triggers: death, disability, divorce, departure, deadlock",
      "Valuation method and payment terms on a buy-out",
      "Non-compete and confidentiality among members",
      "Tax matters representative and distributions for taxes",
    ],
    clausesToWatch: [
      {
        clause: "Capital call and the consequence of not funding",
        why: "If a member can decline with no dilution, working members end up funding inventory for a passive member's benefit.",
      },
      {
        clause: "Working members versus passive members",
        why: "Treating labour and capital identically means the member doing demos every weekend earns the same as the one who wired money once.",
      },
      {
        clause: "Deadlock and tiebreak",
        why: "An even split with no tiebreak stops decisions exactly when co-packer scheduling and retailer resets will not wait.",
      },
      {
        clause: "Buy-out valuation",
        why: "A brand's value sits in velocity, distribution and shelf placement rather than assets, so an undefined method invites two wildly different numbers.",
      },
      {
        clause: "Distributions and tax allocations",
        why: "Members can be allocated profit that was spent on inventory, so a distribution provision that ignores working capital creates real hardship.",
      },
      {
        clause: "Transfer to a competitor or industry party",
        why: "Without restrictions, a member's units can end up with a rival brand owner, a distributor or your co-packer.",
      },
    ],
    cpgIssues: [
      "Capital is consumed by production: cans, labels and cartons carry large minimums and long lead times, so a capital call is usually about a purchase order, not a strategy.",
      "Profit on paper sits in inventory and in receivables aged 30 to 90 days with distributors, so members can owe tax on money the company physically does not have.",
      "A member who works the business full time — sales calls, demos, co-packer supervision — is contributing something the capital account does not capture unless you say so.",
      "Deadlock is expensive in a seasonal category: missing a reset window or a summer selling season pushes revenue an entire year.",
      "Buying out a member drains the working capital that funds the next run, so payment terms on a buy-out matter as much as the price.",
      "Friends-and-family money that arrived by wire with no paperwork often becomes an implied membership claim later, which is exactly what a buyer's counsel looks for.",
    ],
    founderMistakes: [
      "Giving a family member units for the first production run with no written terms, then having no way to buy them back when they wanted out.",
      "Setting a 50/50 member split with no tiebreak and losing a retailer reset window to a stalemate.",
      "Allocating profits without any tax distribution provision, so members owed tax on income the company had spent on ingredients.",
      "Writing no capital call procedure and watching one member fund three runs alone with nothing in return.",
    ],
    negotiationPoints: [
      "Ask for a defined capital call with a real consequence for not funding, agreed before anyone needs the money.",
      "Ask for a working-capital reserve that comes ahead of distributions, sized to a full production run.",
      "Ask for vesting or a forfeiture right on units held by any member who is supposed to be working in the business.",
      "Ask for a buy-out valuation method and a payment schedule the company can actually survive.",
      "Ask for a tiebreak that produces a decision inside a production planning cycle.",
    ],
    redFlags: [
      "No capital call procedure in a business that will need one within a year.",
      "Even ownership with no deadlock mechanism.",
      "Units issued to a passive member with no transfer restrictions.",
      "Mandatory distributions with no reserve for inventory.",
      "No buy-out trigger for death, divorce or departure.",
    ],
    beforeYouSign: [
      "A capital plan for the next 12 months showing every production and packaging commitment.",
      "A complete list of everyone who has given the business money, in any form, and what they were told they were getting.",
      "Agreement on which members are working members and what that means.",
    ],
    related: [
      "llc-operating-agreement",
      "written-consent-of-members",
      "buy-sell-agreement",
      "co-founder-agreement",
      "vesting-agreement",
      "membership-interest-purchase-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "corporate-bylaws",
    name: "Corporate Bylaws",
    category: "formation",
    subcategory: "Corporate governance",
    aliases: ["Bylaws", "Company Bylaws", "Corporate Governance Rules"],
    stages: ["idea", "pre-launch", "fundraising"],
    importance: "essential",
    entityTypes: ["corporation"],
    parties: "Corporation (adopted by the board)",
    whenYouNeedIt:
      "Immediately after incorporating, alongside the first board consent that appoints officers and issues founder stock.",
    whatItDoes:
      "Sets the corporation's internal rules: board size and meetings, notice and quorum, officer roles and authority, how stock is issued and recorded, and how the rules themselves get amended.",
    whyItMatters:
      "Incorporating creates the corporation; the bylaws are what let it act — appoint an officer who can sign the co-packer agreement, approve a stock issuance, open a bank account, authorise a retailer vendor form. Missing or inconsistent bylaws show up twice: in vendor and bank onboarding, where somebody wants proof that the person signing can sign, and in diligence, where a buyer reconstructs whether every share and every major contract was properly approved. Rebuilding that record years later is slow and expensive.",
    majorTerms: [
      "Board size, election and removal of directors",
      "Board meetings, notice, quorum and action by written consent",
      "Officer positions, appointment and authority",
      "Shareholder meetings and voting mechanics",
      "Stock issuance, certificates and the stock ledger",
      "Transfer restrictions and legends referenced",
      "Indemnification of directors and officers",
      "Fiscal year and record keeping",
      "How the bylaws are amended",
      "Conflict of interest procedure",
    ],
    clausesToWatch: [
      {
        clause: "Officer authority",
        why: "If it is vague, nobody outside the company knows who can bind it, and a distributor or co-packer will accept whoever signs.",
      },
      {
        clause: "Board size and how it changes",
        why: "A fixed size written now becomes an obstacle when an investor requires a seat, and expanding it under time pressure invites errors.",
      },
      {
        clause: "Action by written consent",
        why: "Without it, routine approvals need meetings and notice you will not hold, and your corporate record develops gaps.",
      },
      {
        clause: "Indemnification of directors and officers",
        why: "A thin provision makes it hard to recruit an outside director and leaves officers exposed to defence costs personally.",
      },
      {
        clause: "Amendment rights",
        why: "Who may change the bylaws determines who effectively controls governance, regardless of the share split.",
      },
      {
        clause: "Stock ledger and issuance mechanics",
        why: "Shares recorded nowhere are the classic diligence finding, and reconstructing the chain years later is slow and expensive.",
      },
    ],
    cpgIssues: [
      "Retailer vendor packets, 3PL contracts and bank onboarding all ask who is authorised to sign, and the answer comes from the bylaws plus an officer record.",
      "Distributors and co-packers rely on titles: a person introduced as VP of Sales who signs a multi-year territory agreement creates a real commercial commitment.",
      "Organic, kosher, non-GMO and facility certifications are held by the corporation and administered by named officers, so title changes need to be recorded and communicated.",
      "Equity promised to the advisor who introduced a distributor has to be approved by the board — and the approval is what a buyer looks for later.",
      "Insurance certificates naming additional insureds for retailers and co-packers are issued to the corporation, and its exact legal name has to match everywhere.",
    ],
    founderMistakes: [
      "Adopting bylaws downloaded for a different state, then finding the mechanics did not match the certificate of incorporation.",
      "Never holding or documenting a single board action after year one, leaving a two-year gap in the record before a raise.",
      "Letting a founder sign a distributor agreement as CEO when no board action ever appointed a CEO.",
      "Issuing shares to early helpers with no board approval and no entry in a stock ledger.",
    ],
    negotiationPoints: [
      "Ask that officer authority is stated clearly enough that a bank or retailer can rely on it without a separate opinion.",
      "Ask for action by written consent so routine approvals actually get documented.",
      "Ask for indemnification and expense advancement strong enough to recruit an outside director later.",
      "Ask that the bylaws and the certificate of incorporation are reviewed together, since inconsistencies between them cause the most trouble.",
    ],
    redFlags: [
      "Bylaws that conflict with the certificate of incorporation on board size, quorum or share classes.",
      "No stock ledger practice described anywhere.",
      "No indemnification of directors and officers.",
      "Placeholders or another company's name left in the document.",
    ],
    beforeYouSign: [
      "The filed certificate of incorporation, so the two documents can be read against each other.",
      "The intended initial board, officers and share split.",
      "A decision on who will actually maintain the stock ledger and the minute book.",
    ],
    related: [
      "board-consent",
      "officer-appointment-resolution",
      "founder-stock-purchase-agreement",
      "shareholder-agreement",
      "indemnification-agreement",
      "written-consent-of-shareholders",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "shareholder-agreement",
    name: "Shareholder Agreement",
    category: "formation",
    subcategory: "Corporate governance",
    aliases: ["Founders Shareholder Agreement", "Owners Agreement", "Share Ownership Agreement"],
    stages: ["idea", "growth", "fundraising"],
    importance: "essential",
    entityTypes: ["corporation"],
    parties: "Corporation + its Shareholders",
    whenYouNeedIt:
      "Once more than one person holds stock — founders, an early friends-and-family holder, or a helper who took shares instead of an invoice.",
    whatItDoes:
      "Sets the deal among the owners rather than the internal rules of the company: who can sell shares and to whom, who sits on the board, what information holders get, and what happens on a death, a departure or a sale of the business.",
    whyItMatters:
      "CPG exits are usually a sale of the whole company or the whole brand to a strategic buyer, and a buyer wants every share to move at once. One small holder who cannot be found, or who simply says no, can delay a deal or reduce its price. This is also the document that keeps stock from drifting to a competitor, a distributor or an ex-spouse while you are focused on getting a truck loaded.",
    majorTerms: [
      "Transfer restrictions and permitted transferees",
      "Right of first refusal on any proposed sale",
      "Tag-along rights for minority holders",
      "Drag-along obligation on a company sale",
      "Board composition and how directors are chosen",
      "Information rights and reporting frequency",
      "Pre-emptive rights on new issuances",
      "Repurchase rights on departure or death",
      "Confidentiality and non-compete for shareholder-employees",
      "Dispute resolution and deadlock",
    ],
    clausesToWatch: [
      {
        clause: "Drag-along",
        why: "It decides whether a single holder can block a brand sale, and equally whether you can be forced into one on someone else's terms.",
      },
      {
        clause: "Transfer restrictions",
        why: "Without a bar on industry transferees, shares can end up with a rival brand owner, your distributor or your co-packer.",
      },
      {
        clause: "Repurchase on departure",
        why: "If the company has no right to buy back stock from someone who left, the cap table keeps a dead weight through every future round.",
      },
      {
        clause: "Board composition",
        why: "Who controls the board controls approvals for equity grants, debt, guarantees and the sale of the company.",
      },
      {
        clause: "Information rights",
        why: "Too broad and you are sending cost and margin detail to a shareholder who also invests in a competing brand.",
      },
      {
        clause: "Pre-emptive rights",
        why: "Small holders with participation rights can complicate a fast financing when you need to close before a production deposit is due.",
      },
    ],
    cpgIssues: [
      "Strategic acquirers in food and beverage generally want all of the equity, so the drag and transfer mechanics decide whether a deal is even executable.",
      "Early CPG cap tables collect people who bought in at a farmers market or a launch party for a few thousand dollars, and every one of them has to sign at exit.",
      "Shareholders who invest across a category may hold a competing brand, which makes information rights a genuine confidentiality question about your cost sheet and velocity data.",
      "A shareholder who is also your broker, distributor or co-packer contact creates a conflict every time pricing or exclusivity is discussed.",
      "Shares that pass to a spouse in a divorce or to an estate on death are the two transfers founders never plan for and buyers always find.",
    ],
    founderMistakes: [
      "Selling small stakes to friends during launch with nothing but an email, then spending months tracking them down during a sale process.",
      "Having no repurchase right, so a co-founder who left in year one held the same stake through two raises.",
      "Granting broad information rights to an early investor who also backed a competing brand in the same set.",
      "Assuming the bylaws covered transfer restrictions, and learning at diligence that they did not.",
    ],
    negotiationPoints: [
      "Ask for transfer restrictions that expressly block transfers to competitors, distributors, co-packers and their affiliates without consent.",
      "Ask for a company repurchase right triggered by a shareholder leaving the business.",
      "Ask for a drag threshold that works for a brand sale but does not let a bare majority force a bad deal.",
      "Ask that information rights are scoped, so cost and customer data does not travel to a shareholder with category conflicts.",
      "Ask for a mechanism to handle death and divorce transfers before they happen.",
    ],
    redFlags: [
      "Freely transferable shares with no consent requirement.",
      "No drag-along in a company that intends to be acquired.",
      "No repurchase right on any departure.",
      "Board composition that does not match what shareholders believe they agreed.",
      "Information rights broad enough to hand a competitor your landed cost.",
    ],
    beforeYouSign: [
      "A complete, current cap table with every holder, their contact details and how their shares were issued.",
      "The bylaws and certificate of incorporation, so the agreement can be read against them.",
      "A list of shareholders with any competing or industry relationship.",
    ],
    related: [
      "stockholders-agreement",
      "stock-transfer-restriction-agreement",
      "right-of-first-refusal-agreement",
      "corporate-bylaws",
      "buy-sell-agreement",
      "cap-table-documentation",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "stockholders-agreement",
    name: "Stockholders Agreement",
    category: "formation",
    subcategory: "Corporate governance",
    aliases: ["Investor Stockholders Agreement", "Combined Stockholders Agreement"],
    stages: ["fundraising", "growth", "exit"],
    importance: "common",
    entityTypes: ["corporation"],
    parties: "Corporation + Founders + Investors",
    whenYouNeedIt:
      "At a priced financing round, when the company has more than one class of stock and outside investors need their governance and transfer terms in one place.",
    whatItDoes:
      "Combines voting, transfer, first refusal, co-sale and drag terms across all classes of stock into a single agreement signed by the founders and the investors together.",
    whyItMatters:
      "Once outside money is in, the terms of this agreement decide who picks the board, whether you can sell secondary shares to cover a personal loan you took to fund inventory, and whether an acquisition can be forced through or blocked. Founders sign it during a round when the pressure is on the closing date and the money is needed for a production deposit, then live under it for years. Read the drag, the board and the transfer terms before the wire matters more than the paper.",
    majorTerms: [
      "Board composition and voting obligations for director seats",
      "Drag-along on an approved sale",
      "Right of first refusal and co-sale on founder transfers",
      "Transfer restrictions and permitted transferees",
      "Protective provisions requiring investor consent",
      "Information and inspection rights",
      "Pre-emptive rights on future issuances",
      "Founder lock-ups and secondary sale limits",
      "Confidentiality obligations of holders",
      "Termination on an initial public offering or sale",
    ],
    clausesToWatch: [
      {
        clause: "Protective provisions",
        why: "A long consent list can mean an investor must approve routine commercial commitments such as new debt or a large supply contract.",
      },
      {
        clause: "Drag-along threshold",
        why: "Set low, founders can be pulled into a brand sale they think is early; set high, one holder can stall an exit.",
      },
      {
        clause: "Board seats and voting covenants",
        why: "This is where actual control changes hands, more than in the ownership percentages founders focus on.",
      },
      {
        clause: "Founder transfer restrictions",
        why: "Tight limits can prevent a founder from selling any shares even after years of personally funding the business.",
      },
      {
        clause: "Information rights and confidentiality",
        why: "Investors in the same category will receive your cost, velocity and distribution data unless confidentiality is real.",
      },
      {
        clause: "Co-sale rights",
        why: "Investors joining any founder sale can shrink the proceeds a founder expected from a secondary transaction.",
      },
    ],
    cpgIssues: [
      "Protective provisions written for software companies can catch ordinary CPG commitments: a co-packer minimum, an equipment lease or a seasonal credit line.",
      "Category investors and strategic corporate venture arms may back competing brands, so what they receive under information rights is a live commercial issue.",
      "A strategic acquirer buying a brand wants every share, so drag mechanics and signature logistics are exit-critical.",
      "Founders in CPG often carry personal debt from funding early production, and transfer restrictions decide whether a secondary sale can ever repay it.",
      "An investor board designee may sit on other food and beverage boards, creating conflicts around co-packer capacity, ingredient supply and shared brokers.",
    ],
    founderMistakes: [
      "Signing at the close without reading the protective provisions, then needing investor consent for an equipment lease during a capacity crunch.",
      "Accepting a low drag threshold and being pulled into a sale two years earlier than intended.",
      "Agreeing to unrestricted information rights for an investor who later funded a direct competitor.",
      "Not realising the board changed from founder-controlled to investor-influenced on closing.",
    ],
    negotiationPoints: [
      "Ask for consent thresholds set at dollar levels that exclude routine production and supply commitments.",
      "Ask for a drag threshold requiring both a class vote and a board majority, not a bare percentage.",
      "Ask for confidentiality obligations and a competitor carve-out attached to information rights.",
      "Ask for a limited founder secondary allowance so a founder can recover personally advanced funds.",
      "Ask that board observer and designee rights come with conflict disclosure.",
    ],
    redFlags: [
      "Consent rights over ordinary purchase orders or supply agreements.",
      "Information rights with no confidentiality obligation at all.",
      "Drag rights exercisable by a single holder.",
      "Board composition that hands control to investors at a small round size.",
      "No termination or step-down of restrictions over time.",
    ],
    beforeYouSign: [
      "The full financing package read together: charter, purchase agreement, investor rights and this agreement.",
      "A current cap table showing post-close ownership and board composition.",
      "A list of the investor's other holdings in your category.",
    ],
    related: [
      "shareholder-agreement",
      "voting-agreement",
      "rofr-co-sale-agreement",
      "investor-rights-agreement",
      "right-of-first-refusal-agreement",
      "cap-table-documentation",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "voting-agreement",
    name: "Voting Agreement",
    category: "formation",
    subcategory: "Corporate governance",
    aliases: ["Board Voting Agreement", "Shareholder Voting Agreement"],
    stages: ["fundraising", "growth", "exit"],
    importance: "situational",
    entityTypes: ["corporation"],
    parties: "Corporation + Shareholders bound to vote",
    whenYouNeedIt:
      "When investors want a guaranteed board seat, when founders want to vote as a bloc, or when a financing requires everyone to support a future sale.",
    whatItDoes:
      "Obliges named shareholders to vote their shares a particular way — usually to elect specified directors and to support a sale approved under the agreed process.",
    whyItMatters:
      "Board composition decides who approves equity grants, debt, personal guarantees, large supply commitments and ultimately the sale of the brand. A voting agreement changes who effectively controls those approvals without changing anyone's ownership percentage, which is why founders often miss it. The drag element also determines whether you can be required to sell your shares in an acquisition you did not choose.",
    majorTerms: [
      "Which directors each party is obliged to elect",
      "Size of the board and any independent seat",
      "Obligation to vote for an approved company sale",
      "Removal and replacement of designated directors",
      "Board observer rights, if any",
      "Duration and termination triggers",
      "Proxy or attorney-in-fact backup mechanism",
      "Transferees being bound by the same obligations",
    ],
    clausesToWatch: [
      {
        clause: "Drag obligation to vote for a sale",
        why: "It can require you to support and sign up to an acquisition of the brand on terms decided by others.",
      },
      {
        clause: "Independent director selection",
        why: "Whoever nominates the independent seat usually holds the tiebreak on every contested decision.",
      },
      {
        clause: "Irrevocable proxy",
        why: "A backup proxy can allow your shares to be voted without you being in the room at all.",
      },
      {
        clause: "Duration and termination",
        why: "An agreement with no end date keeps binding you long after the investor who required it has been diluted away.",
      },
      {
        clause: "Binding transferees",
        why: "If new holders are not bound, the board arrangement quietly stops working as shares move.",
      },
    ],
    cpgIssues: [
      "Board approvals in CPG cover things that stop production: a co-packer minimum commitment, an equipment lease, a seasonal credit line or a personal guarantee.",
      "An investor designee who sits on several beverage or snack boards may have a real conflict over shared co-packer capacity, broker relationships and ingredient supply.",
      "Strategic acquirers want unanimous shareholder support for a brand sale, so the sale-vote obligation directly affects whether an exit can close cleanly.",
      "Independent directors in this industry are usually category veterans with current relationships at retailers and distributors, which cuts both ways.",
      "Founders who lose the board while keeping majority ownership often discover it when a grant to a key sales hire is refused.",
    ],
    founderMistakes: [
      "Signing a voting agreement as an afterthought at a closing and losing the board majority the same day.",
      "Agreeing that the investor nominates the independent seat, giving them effective control of contested votes.",
      "Leaving the agreement open-ended, so it kept applying after the investor's stake had become small.",
      "Not binding transferees, so a share sale unwound the board structure they had negotiated for.",
    ],
    negotiationPoints: [
      "Ask that the independent seat is chosen by mutual agreement rather than nominated by one side.",
      "Ask for termination on a defined event — a later financing, a sale, or the investor falling below a stake threshold.",
      "Ask that the sale-vote obligation is tied to the same threshold as the drag in your other documents, not a lower one.",
      "Ask for conflict disclosure from any designee who serves on other boards in your category.",
    ],
    redFlags: [
      "An irrevocable proxy with no expiry.",
      "A sale-vote obligation triggered by a single holder's decision.",
      "No termination provision of any kind.",
      "Board terms that conflict with the bylaws or the stockholders agreement.",
    ],
    beforeYouSign: [
      "A board map showing who elects whom before and after this agreement takes effect.",
      "The bylaws and any stockholders agreement, read together for conflicts.",
      "The designee's other board seats and category relationships.",
    ],
    related: [
      "stockholders-agreement",
      "shareholder-agreement",
      "board-consent",
      "independent-director-agreement",
      "investor-rights-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "stock-purchase-agreement",
    name: "Stock Purchase Agreement",
    category: "formation",
    subcategory: "Equity issuance",
    aliases: ["Common Stock Purchase Agreement", "Share Purchase Agreement", "SPA"],
    stages: ["idea", "pre-launch", "fundraising"],
    importance: "common",
    entityTypes: ["corporation"],
    parties: "Corporation + Purchaser of shares",
    whenYouNeedIt:
      "When the company issues shares to a person for cash, for assets or for past services — a founder, an early helper, or a friend writing a small cheque. This is the company selling its own shares, not a venture round on preferred stock and not the sale of a company in an acquisition.",
    whatItDoes:
      "Documents the issuance: how many shares, at what price, what the purchaser is paying with, what each side represents, and what restrictions travel with the shares.",
    whyItMatters:
      "Equity that exists only in an email is the single most common defect a buyer finds in a small CPG company, and it is expensive precisely because it is found late — during a sale, when everyone whose name is on that email has leverage. Selling shares is also a regulated transaction whatever the amount, so the informal cheque taken at a launch party is not a smaller version of a financing, it is an undocumented one. Cleaning this up later costs more than papering it now.",
    majorTerms: [
      "Number and class of shares issued",
      "Purchase price and how it is paid",
      "Consideration other than cash, including assets or past services",
      "Representations from the company and the purchaser",
      "Transfer restrictions and legends on the shares",
      "Right of first refusal and company repurchase rights",
      "Vesting, if the shares are subject to it",
      "Board approval and the stock ledger entry",
      "Closing conditions and delivery mechanics",
    ],
    clausesToWatch: [
      {
        clause: "Consideration and how it is actually paid",
        why: "Shares recorded as issued but never paid for are a defect that shows up in diligence and can unwind an agreed price.",
      },
      {
        clause: "Transfer restrictions and legends",
        why: "Without them the purchaser can resell to anyone, including someone in your category, and you inherit a shareholder you never chose.",
      },
      {
        clause: "Purchaser representations",
        why: "These exist to establish who the buyer is and what they were told, which matters if the relationship sours later.",
      },
      {
        clause: "Company repurchase right",
        why: "Without one, shares issued to someone who stops contributing stay outstanding through every future round.",
      },
      {
        clause: "Assets contributed as payment",
        why: "If a recipe or a brand name is the consideration, the assignment has to actually happen and be described, or the company paid shares for nothing.",
      },
    ],
    cpgIssues: [
      "Shares issued for a contributed recipe, brand name or artwork must be paired with a real assignment of those assets, listed item by item, including any trademark application in a personal name.",
      "Small cheques taken at farmers markets, launch parties and from family for the first production run become permanent cap-table lines and permanent signature requirements at exit.",
      "Equity issued to a broker, a distributor contact or a co-packer relationship in place of fees changes that commercial relationship and needs to be disclosed to future investors.",
      "A person who paid a supplier or label printer directly instead of wiring the company often believes they bought equity; write down what actually happened before memories diverge.",
      "Strategic acquirers of brands do full chain-of-title work on every share, and unclear issuances become escrow, indemnity or price adjustments.",
    ],
    founderMistakes: [
      "Promising two percent to a family friend for the launch run and never issuing anything, then facing a claim during a raise.",
      "Issuing shares with no board approval and no stock ledger entry, so nothing in the record shows they exist.",
      "Taking a cheque and sending a thank-you email instead of a signed agreement.",
      "Issuing shares for a recipe without any assignment document, so the company still did not own the formula.",
    ],
    negotiationPoints: [
      "Ask that every issuance is board approved and entered on the stock ledger the same week.",
      "Ask that any non-cash consideration is described precisely, with an assignment attached.",
      "Ask for transfer restrictions and a company repurchase right on all early issuances.",
      "Ask counsel how small friends-and-family issuances should be handled before you take the money, not after.",
    ],
    redFlags: [
      "Shares agreed by email or handshake with no signed document.",
      "Consideration described as services already performed, with no detail.",
      "No transfer restriction or legend of any kind.",
      "No board approval referenced anywhere.",
      "A purchaser who wants shares issued to an entity or person other than the one paying.",
    ],
    beforeYouSign: [
      "A current cap table showing the position before and after this issuance.",
      "The board approval for this specific issuance.",
      "For non-cash consideration, a written schedule of exactly what is being transferred to the company.",
    ],
    related: [
      "founder-stock-purchase-agreement",
      "restricted-stock-purchase-agreement",
      "board-consent",
      "stock-transfer-restriction-agreement",
      "cap-table-documentation",
      "subscription-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Issuing shares is a securities transaction with tax consequences for both sides, and some tax elections tied to the issuance date have short windows that are easy to miss — involve counsel and a tax adviser before signing rather than after.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "restricted-stock-purchase-agreement",
    name: "Restricted Stock Purchase Agreement",
    category: "formation",
    subcategory: "Equity issuance",
    aliases: ["Restricted Stock Agreement", "RSPA", "Stock Purchase with Vesting"],
    stages: ["idea", "pre-launch", "growth"],
    importance: "essential",
    entityTypes: ["corporation"],
    parties: "Corporation + Recipient of restricted shares",
    whenYouNeedIt:
      "When you issue shares to a founder, an early employee or a long-term helper and want the company to be able to take them back if that person stops contributing.",
    whatItDoes:
      "Issues shares immediately but subject to a repurchase right that lapses over a vesting schedule, so the recipient earns full ownership by staying and working.",
    whyItMatters:
      "Early CPG teams are lopsided and part-time: the friend who runs weekend demos, the operations contact who manages the co-packer, the designer who did the label for equity. Restricted stock is how you keep the cap table matched to who is actually still there, and the repurchase right is the only clean way to recover equity from someone who leaves after one production cycle. Without it, a person who worked four months holds the same stake as the person who worked four years, and every future investor prices that in.",
    majorTerms: [
      "Number of shares issued and price paid",
      "Vesting schedule, cliff and start date",
      "Company repurchase right and repurchase price",
      "Acceleration on a sale of the company, if any",
      "Treatment on termination, with and without cause",
      "Transfer restrictions during the restricted period",
      "Voting and dividend rights while unvested",
      "Escrow or holding arrangements for unvested shares",
      "Board approval and the stock ledger entry",
    ],
    clausesToWatch: [
      {
        clause: "Vesting start date",
        why: "In CPG the real work often began a year before incorporation, and a start date at formation quietly erases that time.",
      },
      {
        clause: "Repurchase price on unvested shares",
        why: "If the price is not clearly the original purchase price, the company may have to find cash it does not have to reclaim its own equity.",
      },
      {
        clause: "Acceleration on a sale",
        why: "Full acceleration can make a brand less attractive to an acquirer who wants the team to stay through the transition.",
      },
      {
        clause: "Definition of cause and of termination",
        why: "Vague definitions turn a departure into a dispute over a meaningful block of equity.",
      },
      {
        clause: "Transfer restrictions during vesting",
        why: "Unvested shares moving to a third party makes the repurchase right hard to exercise and confuses the ledger.",
      },
    ],
    cpgIssues: [
      "Founding work in this category is slow: formulation, shelf-life work and co-packer qualification often run a year before the first case ships, so vesting dates and credit for prior work deserve real thought.",
      "Early contributors are frequently part time — demo leads, a weekend sales help, a designer taking equity for the label — and vesting keeps their stake proportional to what they actually deliver.",
      "A large retailer authorization changes what the company obviously looks worth, so the valuation conversation around equity issued afterwards gets harder; that timing question belongs with counsel and a tax adviser.",
      "The repurchase right is what lets you recover equity from a co-founder who leaves after a failed run or a co-packer split, which is a common early failure point.",
      "Equity handed to the operations person who holds the co-packer relationship should vest, or their departure takes both the relationship and the stake.",
    ],
    founderMistakes: [
      "Issuing shares outright to an early helper with no vesting, then having no way to recover them when the person disengaged after launch.",
      "Setting vesting to start at incorporation and ignoring two years of prior formulation work by one founder.",
      "Leaving cause undefined, so a contested exit became a negotiation over the unvested block.",
      "Missing the paperwork deadlines around the issuance because nobody told the recipient there were any.",
    ],
    negotiationPoints: [
      "Ask for a vesting start date that credits documented pre-formation work, and write down what that work was.",
      "Ask that the repurchase price for unvested shares equals what was paid, so the right is actually usable.",
      "Ask for a defined, written cause standard rather than a general one.",
      "Ask whether acceleration should be partial and tied to a termination after a sale rather than to the sale itself.",
      "Ask counsel and a tax adviser about the filing steps that follow the issuance, at the time of issuance.",
    ],
    redFlags: [
      "No repurchase right at all on shares issued to a working recipient.",
      "A repurchase price set at fair value, making the right unaffordable for an early-stage company.",
      "Full single-trigger acceleration on every grant.",
      "No board approval or ledger entry accompanying the issuance.",
    ],
    beforeYouSign: [
      "The current cap table and the fully diluted position after this grant.",
      "A written vesting schedule everyone has read, including the start date and the cliff.",
      "The board approval authorising the issuance.",
    ],
    related: [
      "founder-stock-purchase-agreement",
      "founder-vesting-agreement",
      "vesting-agreement",
      "stock-purchase-agreement",
      "equity-incentive-plan",
      "board-consent",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Restricted equity carries both securities and tax consequences, and there are time-limited elections tied to the date the shares are issued whose deadlines are short and easy to miss — get counsel and a tax adviser involved at the grant, not later.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "founder-stock-purchase-agreement",
    name: "Founder Stock Purchase Agreement",
    category: "formation",
    subcategory: "Equity issuance",
    aliases: ["Founder Stock Agreement", "Common Stock Purchase Agreement (Founder)", "Founder Share Issuance"],
    stages: ["idea", "pre-launch"],
    importance: "essential",
    entityTypes: ["corporation"],
    parties: "Corporation + Founder",
    whenYouNeedIt:
      "In the first weeks after incorporating, alongside the board consent authorising the issuance and the founder's IP assignment.",
    whatItDoes:
      "Actually issues founder shares: states how many, what the founder pays, what the founder assigns to the company as part of the deal, and the vesting and repurchase terms that apply.",
    whyItMatters:
      "Founders routinely believe they own stock because the incorporation paperwork mentioned shares. Shares exist when the board approves them, the founder pays for them and the ledger records them — and the third thing a buyer checks is whether the founder's contributed recipe and brand name came across at the same time. This is the single document that ties equity, vesting and IP ownership together, and it is far cheaper to sign in week one than to reconstruct during diligence four years later.",
    majorTerms: [
      "Number of shares and price per share",
      "How the founder pays, including assignment of IP as consideration",
      "Schedule of assigned assets: recipes, formulas, brand name, artwork, domains, accounts",
      "Vesting schedule, cliff and start date",
      "Company repurchase right over unvested shares",
      "Treatment on departure and on a sale of the company",
      "Transfer restrictions and legends",
      "Spousal consent, where relevant",
      "Board approval and stock ledger entry",
    ],
    clausesToWatch: [
      {
        clause: "The assigned assets schedule",
        why: "If it is generic, the trademark filed in a personal name and the recipe notebooks stay outside the company and turn up in diligence.",
      },
      {
        clause: "Vesting start date and credit for prior work",
        why: "Founders who spent a year formulating before incorporating lose that time unless the document says otherwise.",
      },
      {
        clause: "Repurchase right and its price",
        why: "This is the mechanism that recovers equity from a departing founder; if it is missing, nothing does.",
      },
      {
        clause: "Payment for the shares",
        why: "Founder shares recorded as issued but never paid for are a recurring diligence finding that costs deal time.",
      },
      {
        clause: "Acceleration on an acquisition",
        why: "Acquirers of brands usually want founders to stay through transition, and full acceleration works against that.",
      },
    ],
    cpgIssues: [
      "The most valuable page in the file is the assignment schedule: every recipe iteration, formulation note, cost sheet, label artwork file, product photograph, domain and social handle, listed by name.",
      "A trademark application or registration in a founder's personal name usually needs its own properly handled assignment rather than a single sentence in a general agreement — ask counsel to do it correctly.",
      "Artwork a founder paid a freelancer for personally may never have been assigned to the founder in the first place, so the chain has to be repaired at the freelancer level before it can move to the company.",
      "Seller accounts, ad accounts and the GS1 company prefix registered to a founder personally do not travel with the shares unless someone moves them.",
      "In a brand acquisition, unassigned founder IP is not a technicality — it is the asset being bought, and it becomes an indemnity or a price reduction.",
    ],
    founderMistakes: [
      "Assuming incorporation issued the shares, and discovering at a raise that no purchase agreement or ledger entry ever existed.",
      "Signing a one-line IP assignment that never mentioned the trademark application filed personally six months earlier.",
      "Skipping vesting between two founders who trusted each other, then having no mechanism when one left in month five.",
      "Missing the short filing windows that follow the issuance because nobody flagged that they existed.",
    ],
    negotiationPoints: [
      "Ask that every contributed asset is scheduled by name, with the trademark filings handled by counsel as separate assignments.",
      "Ask for vesting on all founders including yourself, with a documented start date reflecting real prior work.",
      "Ask that the repurchase price for unvested shares is the original purchase price.",
      "Ask for the board approval and the ledger entry to be completed in the same sitting as signature.",
      "Ask counsel and a tax adviser at the time of issuance what steps and deadlines follow it.",
    ],
    redFlags: [
      "No assigned assets schedule attached.",
      "No vesting on any founder.",
      "Shares treated as issued with no payment and no board approval.",
      "A founder who declines to assign the brand name or the formula they developed.",
      "Blank exhibits carried over from a template.",
    ],
    beforeYouSign: [
      "A written inventory of every asset the brand depends on and whose name it currently sits in.",
      "The filed certificate of incorporation and the bylaws.",
      "The board consent authorising the issuance and setting the price.",
    ],
    related: [
      "founder-ip-assignment",
      "founder-vesting-agreement",
      "restricted-stock-purchase-agreement",
      "board-consent",
      "corporate-bylaws",
      "founder-stock-documents",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Founder equity carries securities and tax consequences, and elections tied to the issuance date run on short deadlines that founders routinely miss — have counsel and a tax adviser handle this at formation rather than at the first financing.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "stock-transfer-agreement",
    name: "Stock Transfer Agreement",
    category: "formation",
    subcategory: "Share transfers",
    aliases: ["Share Transfer Agreement", "Stock Assignment", "Secondary Share Sale"],
    stages: ["growth", "fundraising", "exit"],
    importance: "situational",
    entityTypes: ["corporation"],
    parties: "Selling Shareholder + Buying Shareholder or Company",
    whenYouNeedIt:
      "When existing shares move between people: a founder buying out a departed co-founder, an early holder cashing out before a round, or a transfer into a trust or a family member's name.",
    whatItDoes:
      "Transfers shares from one holder to another and records the price, the representations from both sides, the consents obtained and the update to the company's stock ledger.",
    whyItMatters:
      "Most small CPG companies have no reliable share record, so every transfer that is not documented breaks the chain a buyer will later try to trace. The commercial risk is concrete: a transfer that skipped a required consent or first-refusal step can hold up a financing or an acquisition until it is unwound and redone, usually while a term sheet has an expiry date on it.",
    majorTerms: [
      "Shares transferred, class and certificate or book-entry reference",
      "Price and payment terms",
      "Consents required from the board or other shareholders",
      "Compliance with first refusal and co-sale procedures",
      "Representations on title and on any encumbrances",
      "Release of claims between the parties, if agreed",
      "Agreement by the transferee to be bound by existing shareholder agreements",
      "Stock ledger update and certificate cancellation",
    ],
    clausesToWatch: [
      {
        clause: "Transferee joining the existing agreements",
        why: "If the buyer does not sign up to the shareholder and transfer restrictions, your governance quietly stops applying to those shares.",
      },
      {
        clause: "Waiver of first refusal and co-sale",
        why: "Skipping the waiver step makes the transfer questionable later and forces a clean-up in the middle of a deal.",
      },
      {
        clause: "Representations on title",
        why: "Shares pledged for a personal loan or subject to a divorce claim carry problems that follow them to the new holder.",
      },
      {
        clause: "Release of claims",
        why: "Buying out a departing founder without a release leaves the old dispute alive alongside the new cap table.",
      },
      {
        clause: "Payment terms on an instalment purchase",
        why: "An unpaid balance to a former shareholder becomes a company or founder liability that a buyer will find and deduct.",
      },
    ],
    cpgIssues: [
      "Shares drifting to someone who owns or works for a competing brand, a distributor or your co-packer creates a conflict at exactly the wrong table.",
      "Buying out early friends-and-family holders before a raise is common in CPG, and doing it without documented consent steps causes the clean-up to land during the round.",
      "Small companies often pay a departing holder in instalments funded by cash flow that is actually committed to the next production run.",
      "The founder whose personal guarantee still sits on a co-packer or equipment agreement needs that addressed when their shares move, or they carry exposure with no upside.",
      "Acquirers reconstruct chain of title for every share of a brand they buy, and gaps become escrow holdbacks rather than conversations.",
    ],
    founderMistakes: [
      "Buying out a co-founder with a handshake and a bank transfer, with no assignment, no release and no ledger entry.",
      "Letting an early holder sell to an acquaintance without checking whether that person had a competing interest.",
      "Never updating the ledger after several transfers, so the cap table used to price a round was wrong.",
      "Agreeing instalment payments that collided with a co-packer deposit and left the run unfunded.",
    ],
    negotiationPoints: [
      "Ask that the transferee signs on to every existing shareholder agreement as a condition of the transfer.",
      "Ask for a mutual release when the transfer resolves a departure.",
      "Ask for written waivers of any first refusal and co-sale rights before closing.",
      "Ask that any personal guarantees held by the seller are addressed in the same document.",
    ],
    redFlags: [
      "A transfer proceeding with no board or shareholder consent obtained.",
      "A transferee refusing to be bound by the existing agreements.",
      "No representation that the shares are free of pledges or claims.",
      "Payment terms the company cannot support alongside its production schedule.",
    ],
    beforeYouSign: [
      "The current stock ledger and cap table.",
      "The shareholder, transfer restriction and first refusal documents that apply to these shares.",
      "Written consents or waivers from everyone whose rights are triggered.",
    ],
    related: [
      "stock-transfer-restriction-agreement",
      "right-of-first-refusal-agreement",
      "shareholder-agreement",
      "shareholders-buy-sell-agreement",
      "cap-table-documentation",
      "general-release",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "stock-transfer-restriction-agreement",
    name: "Stock Transfer Restriction Agreement",
    category: "formation",
    subcategory: "Share transfers",
    aliases: ["Transfer Restriction Agreement", "Share Restriction Agreement", "Stock Restriction Agreement"],
    stages: ["idea", "pre-launch", "fundraising"],
    importance: "common",
    entityTypes: ["corporation"],
    parties: "Corporation + all Shareholders",
    whenYouNeedIt:
      "As soon as more than one person holds stock, and before you issue shares to anyone outside the founding group.",
    whatItDoes:
      "Restricts how and to whom shares can be transferred: consent requirements, permitted transferees, first refusal steps, legends on the certificates and the company's right to refuse a transfer that skips the process.",
    whyItMatters:
      "Shares move for reasons founders never plan for — a death, a divorce, a holder who needs cash, a friend who sells to someone they met at a trade show. In a small category where everyone knows everyone, an unrestricted share can end up with a competing brand owner, a distributor or the co-packer running your line. Restrictions cost nothing to put in place at the start and cannot be imposed on holders after the fact without their agreement.",
    majorTerms: [
      "Transfers requiring board or shareholder consent",
      "Permitted transferees such as trusts and immediate family",
      "First refusal procedure and notice periods",
      "Prohibited transferees, including competitors and industry parties",
      "Legends placed on certificates or book entries",
      "Company right to refuse to record a non-compliant transfer",
      "Treatment of involuntary transfers on death, divorce or bankruptcy",
      "Term and termination of the restrictions",
    ],
    clausesToWatch: [
      {
        clause: "Definition of a competitor or industry party",
        why: "Written too narrowly it misses a distributor or a co-packer affiliate; too broadly and legitimate transfers get blocked.",
      },
      {
        clause: "Involuntary transfers",
        why: "Death, divorce and creditor claims move shares without anyone signing anything, and only this clause anticipates it.",
      },
      {
        clause: "Notice periods in the first refusal process",
        why: "Windows that are too short favour whoever is organised; too long and a legitimate sale collapses.",
      },
      {
        clause: "Permitted transferees",
        why: "A category broad enough to include an unrelated entity lets a holder route around every other restriction.",
      },
      {
        clause: "Company refusal to record a transfer",
        why: "Without an explicit right to refuse, the restriction has no practical teeth at the ledger.",
      },
    ],
    cpgIssues: [
      "Food and beverage is a small industry: a shareholder who joins a competitor, a broker house or a distributor becomes an information problem overnight.",
      "Shareholders often receive cost, velocity and account-level detail, which is exactly what you do not want travelling with a share sale.",
      "Early holders in CPG are frequently friends and family with no plan to hold long term, so a first refusal step is used more often than founders expect.",
      "A co-packer or ingredient supplier taking equity in lieu of payment should be treated as an industry transferee with restrictions attached from day one.",
      "Acquirers want confirmation that the shares they are buying could not have leaked to a competitor along the way.",
    ],
    founderMistakes: [
      "Issuing shares to a dozen supporters with no restrictions, then finding one had sold to a person building a competing brand.",
      "Relying on the bylaws to restrict transfers when the bylaws said nothing about it.",
      "Excluding involuntary transfers, so a divorce moved a meaningful stake with no process at all.",
      "Trying to add restrictions after the fact and discovering every existing holder had to agree.",
    ],
    negotiationPoints: [
      "Ask that the definition of a restricted transferee expressly covers distributors, brokers, co-packers, suppliers and their affiliates.",
      "Ask for involuntary transfers to trigger the same consent and first refusal process.",
      "Ask for legends applied at issuance so the restriction is visible on every certificate or book entry.",
      "Ask for notice periods that fit how quickly your board can actually meet.",
    ],
    redFlags: [
      "No restriction on transfers to industry participants.",
      "Permitted transferee categories broad enough to include unrelated entities.",
      "No treatment of death, divorce or creditor transfers.",
      "No right for the company to refuse to record a transfer.",
    ],
    beforeYouSign: [
      "A list of every current holder and whether they have any competing industry relationship.",
      "The bylaws and any shareholder agreement, so the restrictions align rather than conflict.",
    ],
    related: [
      "right-of-first-refusal-agreement",
      "shareholder-agreement",
      "stock-transfer-agreement",
      "shareholders-buy-sell-agreement",
      "stockholders-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "right-of-first-refusal-agreement",
    name: "Right of First Refusal Agreement",
    category: "formation",
    subcategory: "Share transfers",
    aliases: ["ROFR Agreement", "First Refusal Rights", "Pre-emptive Purchase Rights"],
    stages: ["pre-launch", "fundraising", "growth"],
    importance: "common",
    entityTypes: ["corporation"],
    parties: "Corporation + Shareholders",
    whenYouNeedIt:
      "When you want the company or the remaining shareholders to have the first chance to buy any stock a holder decides to sell — usually put in place with the shareholder agreement.",
    whatItDoes:
      "Requires a selling shareholder to offer their shares to the company first, then to the other shareholders, on the same terms as the outside offer, before any sale to a third party can close.",
    whyItMatters:
      "This is the practical control on who ends up owning your brand alongside you. It matters most in the situation founders least expect: an early holder gets an offer from someone in the industry and would rather take the cash than manage your feelings about it. The catch is that the right is only useful if someone can actually fund it, and a CPG company with its cash in inventory and receivables usually cannot, which is why the assignment step to other shareholders is the part worth getting right.",
    majorTerms: [
      "Trigger events and what counts as a proposed transfer",
      "Notice requirements and the information the seller must provide",
      "Company exercise window, then shareholder exercise window",
      "Right to assign the option to other shareholders or third parties",
      "Whether partial exercise is permitted",
      "Price and payment terms matching the third-party offer",
      "Closing period once exercised",
      "Consequences of a sale completed without following the process",
      "Exempt or permitted transfers",
    ],
    clausesToWatch: [
      {
        clause: "Exercise windows",
        why: "A window shorter than the time your board takes to meet and fund makes the right decorative.",
      },
      {
        clause: "Assignment of the right",
        why: "If the company cannot pay, the ability to pass the option to other shareholders is what keeps the share from leaving.",
      },
      {
        clause: "Partial exercise",
        why: "Allowing part purchases can leave a fragment of the block sold to the outside buyer anyway, defeating the point.",
      },
      {
        clause: "Matching terms",
        why: "An outside offer with unusual non-cash terms can be structured to make matching impossible.",
      },
      {
        clause: "Consequence of a non-compliant sale",
        why: "Without a stated consequence, the process is advisory and a determined seller will simply proceed.",
      },
    ],
    cpgIssues: [
      "A CPG balance sheet holds its cash as inventory and distributor receivables, so the company rarely has funds sitting ready to exercise on short notice.",
      "The likely outside buyer in this industry is someone already in it — a category investor, a distributor principal, a brand operator — which is exactly the transfer you want the first chance to stop.",
      "Seasonal cash cycles mean an exercise window falling right after a large production deposit can be impossible to meet even when the company wants to.",
      "First refusal and co-sale are different rights; founders frequently believe they have both when the documents only give one.",
      "Buying back an early holder's shares competes directly with funding the next run, so plan how an exercise would be financed before you need it.",
    ],
    founderMistakes: [
      "Agreeing to a short exercise window and losing the right because the board could not convene and fund inside it.",
      "Having no assignment provision, so the option died with the company's inability to pay.",
      "Assuming a first refusal right also gave them co-sale protection in a founder-level sale.",
      "Never sending the required notices, which made the process meaningless when it was finally needed.",
    ],
    negotiationPoints: [
      "Ask for exercise windows measured against your real board and cash cycle, not a generic default.",
      "Ask for the right to assign the option to other shareholders and to the company's designees.",
      "Ask that non-cash or unusual consideration in a third-party offer can be satisfied with a cash equivalent.",
      "Ask for a clear consequence when a sale is attempted without following the process.",
    ],
    redFlags: [
      "No assignment right where the company is unlikely to be able to fund an exercise.",
      "Windows of a few days.",
      "A permitted transfer list broad enough to swallow the right.",
      "No stated remedy for a non-compliant transfer.",
    ],
    beforeYouSign: [
      "A realistic view of how the company would fund an exercise given its production calendar.",
      "The shareholder agreement and transfer restrictions, to confirm the rights work together.",
    ],
    related: [
      "stock-transfer-restriction-agreement",
      "shareholder-agreement",
      "stock-transfer-agreement",
      "rofr-co-sale-agreement",
      "buy-sell-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "buy-sell-agreement",
    name: "Buy-Sell Agreement",
    category: "formation",
    subcategory: "Ownership transitions",
    aliases: ["Business Continuity Agreement", "Owner Buyout Agreement", "Cross-Purchase Agreement"],
    stages: ["pre-launch", "growth", "exit"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + all Owners (members or shareholders)",
    whenYouNeedIt:
      "Once two or more people own the business and it has real value — inventory, distribution and a brand worth arguing about.",
    whatItDoes:
      "Sets what happens to an owner's stake on death, disability, divorce, bankruptcy, retirement or a falling-out: who buys it, how the price is determined, and how the purchase is funded.",
    whyItMatters:
      "The event that triggers this document always arrives at a bad time, and in CPG it arrives while cash is locked in inventory and receivables. Without an agreed price method and funding source, the surviving owners are negotiating with an estate or an ex-spouse while a co-packer run is scheduled and a retailer expects fill. Agreed in advance, the same event is a procedure with a cheque attached.",
    majorTerms: [
      "Trigger events: death, disability, divorce, bankruptcy, retirement, voluntary exit, deadlock",
      "Who buys: the company, the other owners, or both in sequence",
      "Valuation method and how often it is refreshed",
      "Funding mechanism, including life or disability insurance",
      "Payment terms and instalment schedule",
      "Mandatory versus optional purchase for each trigger",
      "Restrictions on transferring while a trigger is pending",
      "Treatment of any personal guarantees the departing owner gave",
      "Dispute resolution over valuation",
    ],
    clausesToWatch: [
      {
        clause: "Valuation method",
        why: "A method built for an asset-heavy business undervalues a brand whose worth sits in velocity, distribution and shelf placement.",
      },
      {
        clause: "Funding source",
        why: "A purchase obligation with no funding plan turns into a note the company must service out of working capital it needs for production.",
      },
      {
        clause: "Mandatory purchase on death",
        why: "A mandatory buy the company cannot fund is a solvency problem, not a succession plan.",
      },
      {
        clause: "Disability definition and waiting period",
        why: "Vague standards create disputes at the moment the business most needs a decision made.",
      },
      {
        clause: "Personal guarantees of the departing owner",
        why: "An owner who exits while still guaranteeing a co-packer or equipment lease keeps the exposure without the upside.",
      },
      {
        clause: "Payment terms",
        why: "Instalments that collide with your production and packaging deposits can stop the business while it is paying for the past.",
      },
    ],
    cpgIssues: [
      "Valuing an early brand is genuinely hard: the value sits in distribution, velocity, repeat rate and shelf placement rather than in equipment or receivables, and buyers in this category price on those things.",
      "The cash to fund a buy-out is usually the same cash that funds the next production run, so payment terms have to be modelled against the production calendar.",
      "If the founder is the face of the brand — on pack, at demos, in the content — their death or departure changes the asset itself, which is why key-person insurance appears in this conversation.",
      "Personal guarantees on co-packer credit, equipment leases and warehouse space commonly sit with one owner and need to be released or indemnified at exit.",
      "A stake that passes to an estate or an ex-spouse who has no interest in the business still has to sign every consent at a future financing or brand sale.",
    ],
    founderMistakes: [
      "Leaving valuation to be agreed at the time, then spending a year and real legal fees arguing about a multiple.",
      "Committing the company to buy a deceased owner's stake with no insurance and no reserve.",
      "Ignoring divorce as a trigger and ending up with a former spouse holding a blocking position.",
      "Buying out a partner on terms that consumed the deposit for the next production run.",
    ],
    negotiationPoints: [
      "Ask for a valuation method suited to a brand: a formula tied to revenue or contribution, with a defined appraiser as the tiebreak.",
      "Ask for insurance funding on the triggers where it is available, and check who owns and pays for the policy.",
      "Ask for payment terms that survive your seasonal cash cycle, with the schedule modelled against production deposits.",
      "Ask that any personal guarantee given by a departing owner is released or indemnified as part of the buy-out.",
      "Ask for the valuation to be refreshed on a set schedule so it does not go stale.",
    ],
    redFlags: [
      "No valuation method, or one to be agreed later.",
      "A mandatory purchase obligation with no funding.",
      "No divorce or bankruptcy trigger.",
      "Payment terms that ignore the working capital the business needs to operate.",
      "No provision addressing the departing owner's guarantees.",
    ],
    beforeYouSign: [
      "A current valuation or at least an agreed method and a recent set of financials.",
      "A list of every personal guarantee, by owner and by counterparty.",
      "A cash model showing what the company could actually pay and over what period.",
    ],
    related: [
      "shareholders-buy-sell-agreement",
      "multi-member-llc-operating-agreement",
      "shareholder-agreement",
      "stock-transfer-agreement",
      "co-founder-agreement",
      "personal-guarantee",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "shareholders-buy-sell-agreement",
    name: "Shareholders' Buy-Sell Agreement",
    category: "formation",
    subcategory: "Ownership transitions",
    aliases: ["Stock Redemption Agreement", "Corporate Buy-Sell Agreement", "Shareholder Buyout Agreement"],
    stages: ["growth", "fundraising", "exit"],
    importance: "common",
    entityTypes: ["corporation"],
    parties: "Corporation + Shareholders",
    whenYouNeedIt:
      "When a corporation with more than one shareholder wants a fixed procedure for buying back stock on a departure, a death or a dispute, integrated with its existing transfer restrictions.",
    whatItDoes:
      "Chooses the structure — the company redeems the shares, the other shareholders buy them, or a sequence of both — and sets the triggers, the price, the funding and how the purchase interacts with the transfer restrictions already in place.",
    whyItMatters:
      "The structure choice decides who ends up in control. A company redemption drains the working capital that funds your next production run but leaves the remaining split unchanged; a cross-purchase preserves company cash but hands more of the company to whichever shareholder has money available. Founders who do not choose deliberately usually discover which one they got in the middle of the event, when the answer is expensive either way.",
    majorTerms: [
      "Redemption by the company versus cross-purchase by shareholders",
      "Order of priority if both routes are available",
      "Trigger events and which are mandatory",
      "Valuation formula and refresh schedule",
      "Insurance funding, policy ownership and beneficiaries",
      "Payment terms, security and interest",
      "Interaction with transfer restrictions and first refusal rights",
      "Treatment of unvested or restricted shares",
      "Effect on board composition after the purchase",
    ],
    clausesToWatch: [
      {
        clause: "Redemption versus cross-purchase",
        why: "One consumes company cash, the other shifts control; picking by default means picking blind.",
      },
      {
        clause: "Interaction with first refusal rights",
        why: "Two overlapping processes with different notice periods create a procedural mess exactly when speed matters.",
      },
      {
        clause: "Valuation formula",
        why: "A stale or asset-based formula produces a number nobody accepts for a brand with distribution and velocity.",
      },
      {
        clause: "Insurance ownership",
        why: "Who owns and pays for the policy changes who receives the proceeds and how the purchase actually gets funded.",
      },
      {
        clause: "Payment security",
        why: "An unsecured instalment obligation to a former shareholder becomes a liability a future investor or acquirer will price.",
      },
      {
        clause: "Effect on board seats",
        why: "Buying out a shareholder without addressing their board seat leaves a governance gap in the middle of a transition.",
      },
    ],
    cpgIssues: [
      "A redemption timed near a large packaging order or a co-packer deposit can leave the company unable to produce, which is a bigger problem than the buy-out itself.",
      "Cross-purchase changes the shareholder split, so the person with liquidity can end up controlling a brand whose growth someone else built.",
      "Key-person cover matters more where the founder appears on the packaging and in the brand's content, because the departure changes the asset.",
      "An unpaid instalment obligation to a former shareholder shows up in diligence as debt and gets deducted from a brand purchase price.",
      "Category shareholders who also hold competing brands make a mandatory buy-out route valuable, since you may want a specific holder out quickly.",
    ],
    founderMistakes: [
      "Letting the company redeem a departing shareholder and having nothing left to fund the season's production.",
      "Running a cross-purchase without noticing it handed effective control to the shareholder who had cash.",
      "Keeping an insurance policy in the wrong name, so the proceeds did not reach the party who had to pay.",
      "Writing a buy-sell that contradicted the first refusal steps in the shareholder agreement.",
    ],
    negotiationPoints: [
      "Ask that the structure is chosen explicitly, with the reasons written down, rather than left to circumstance.",
      "Ask that this agreement and the transfer restriction and first refusal documents are drafted and read together.",
      "Ask for a valuation formula appropriate to a branded consumer business, refreshed annually.",
      "Ask that instalment obligations are subordinated to the working capital the business needs to run.",
      "Ask that board seats and any guarantees are dealt with in the same transaction.",
    ],
    redFlags: [
      "A mandatory company redemption with no insurance and no cash test.",
      "Provisions that conflict with the first refusal process already in place.",
      "No valuation refresh, leaving a formula agreed years earlier.",
      "Insurance referenced but never actually put in place.",
    ],
    beforeYouSign: [
      "Current financials and a valuation the shareholders have all seen.",
      "Confirmation of any insurance in place, its owner and its beneficiary.",
      "The shareholder agreement and transfer restrictions, reviewed alongside this document.",
    ],
    related: [
      "buy-sell-agreement",
      "shareholder-agreement",
      "stock-transfer-restriction-agreement",
      "stock-transfer-agreement",
      "right-of-first-refusal-agreement",
      "corporate-bylaws",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "board-consent",
    name: "Board Consent",
    category: "formation",
    subcategory: "Board & member actions",
    aliases: ["Unanimous Written Consent of the Board", "Action by Written Consent", "Board Approval"],
    stages: ["idea", "pre-launch", "fundraising", "growth"],
    importance: "essential",
    entityTypes: ["corporation"],
    parties: "Corporation + its Directors",
    whenYouNeedIt:
      "Every time the board has to approve something without holding a meeting: issuing stock, granting options, appointing officers, opening bank accounts, approving a major contract or a guarantee.",
    whatItDoes:
      "Records a board decision in writing, signed by the directors, so the approval exists as part of the company's permanent record rather than as a conversation people remember differently.",
    whyItMatters:
      "Equity that the board never approved is the most common cap-table defect a buyer finds in a small CPG company, and a major commitment signed without authority is the second. Both are cheap to prevent — a one-page consent at the time — and expensive to fix, because the fix happens under deal pressure when the counterparty has leverage. This is also the document a bank, a 3PL or a retailer asks for when they want proof that the person signing can sign.",
    majorTerms: [
      "The specific actions being approved",
      "Stock and option issuances, with recipient, amount and price",
      "Officer appointments and their authority",
      "Bank accounts and authorised signatories",
      "Approval of material contracts and any guarantees",
      "Adoption of plans and policies",
      "Ratification of past actions",
      "Effective date and director signatures",
    ],
    clausesToWatch: [
      {
        clause: "Scope of authority granted to an officer",
        why: "A general authorisation lets someone commit the company to obligations the board never contemplated.",
      },
      {
        clause: "Equity approvals and their terms",
        why: "A consent that approves a grant without stating price, vesting and recipient leaves the grant ambiguous in the record.",
      },
      {
        clause: "Ratification language",
        why: "Ratifying past actions is useful clean-up, but only if the actions are actually described rather than swept up generically.",
      },
      {
        clause: "Unanimity requirement",
        why: "If written consent requires every director and one is unreachable, routine approvals stall at exactly the wrong moment.",
      },
      {
        clause: "Effective date",
        why: "A consent dated after the action it approves creates a gap a buyer's counsel will ask about.",
      },
    ],
    cpgIssues: [
      "Board approval is what stands behind an officer signing a co-packer master agreement, a distributor territory deal or a retailer vendor agreement with an indemnity in it.",
      "Personal guarantees demanded by co-packers, equipment lessors and landlords should be board approved, because they create exposure well beyond the contract value.",
      "Equity promised to the advisor who introduced a distributor or the designer who did the label has to be approved by the board, and the approval is what a buyer looks for years later.",
      "Bank, insurance and 3PL onboarding packets routinely request a resolution or consent naming the authorised signer.",
      "Founders raising for the first time discover they need a clean run of consents covering every past issuance, and reconstructing them takes weeks nobody has during a round.",
    ],
    founderMistakes: [
      "Granting options verbally to a first sales hire and never taking a board consent, leaving the grant undocumented for two years.",
      "Signing a multi-year distribution agreement without board approval and with no officer appointment behind the title used.",
      "Keeping no minute book at all, and reconstructing four years of approvals during diligence.",
      "Dating consents long after the fact and creating obvious gaps in the record.",
    ],
    negotiationPoints: [
      "Ask that every equity grant, guarantee and material contract goes on a consent at the time it happens, not in an annual clean-up.",
      "Ask for officer authority to be described by dollar threshold and subject matter rather than generally.",
      "Ask that the consent, the signed agreement and the ledger entry are filed together.",
      "Ask counsel for a standing list of the actions your board should approve, so nothing gets missed between meetings.",
    ],
    redFlags: [
      "Approving an equity grant with no recipient, price or vesting stated.",
      "Blanket authority for one person to bind the company without limit.",
      "Consents signed by fewer directors than the bylaws require.",
      "A long gap in the record covered by a single generic ratification.",
    ],
    beforeYouSign: [
      "The bylaws, to confirm how written consent works and who must sign.",
      "The underlying documents being approved, in final form.",
      "The current cap table, if the consent approves any equity.",
    ],
    related: [
      "board-resolution",
      "corporate-bylaws",
      "officer-appointment-resolution",
      "founder-stock-purchase-agreement",
      "equity-incentive-plan",
      "written-consent-of-shareholders",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "written-consent-of-shareholders",
    name: "Written Consent of Shareholders",
    category: "formation",
    subcategory: "Board & member actions",
    aliases: ["Shareholder Written Consent", "Stockholder Consent", "Action by Written Consent of Stockholders"],
    stages: ["pre-launch", "fundraising", "growth", "exit"],
    importance: "common",
    entityTypes: ["corporation"],
    parties: "Corporation + its Shareholders",
    whenYouNeedIt:
      "When an approval has to come from the owners rather than the board: electing directors, amending the charter, adopting an equity plan, or approving a sale of the company.",
    whatItDoes:
      "Records shareholder approval in writing without holding a meeting, signed by holders of the required percentage of shares.",
    whyItMatters:
      "Two shareholder approvals reliably ambush CPG founders. The first is authorising more shares before a financing — you cannot issue stock you do not have, and discovering that during a closing costs days you do not have when a production deposit is due. The second is at exit, when a sale needs shareholder approval and the friends-and-family holders from launch year have moved, changed emails or lost interest. Both are procedural, and both stop deals.",
    majorTerms: [
      "The action being approved and the required approval threshold",
      "Election or removal of directors",
      "Amendments to the certificate of incorporation",
      "Increase in authorised shares",
      "Adoption or amendment of an equity incentive plan",
      "Approval of a merger or sale of the company",
      "Notice to non-consenting shareholders",
      "Signature blocks and record date",
    ],
    clausesToWatch: [
      {
        clause: "Required threshold",
        why: "Getting the threshold wrong means the approval does not stand, and it usually gets discovered under closing pressure.",
      },
      {
        clause: "Notice to shareholders who did not sign",
        why: "Skipping the notice step leaves a procedural gap a buyer's counsel will insist on curing.",
      },
      {
        clause: "Description of the action",
        why: "A vague description means the consent may not actually cover what was done, so the approval has to be redone.",
      },
      {
        clause: "Record date and who was entitled to consent",
        why: "Using a stale cap table means the wrong people signed, and the consent has to be reconstructed.",
      },
      {
        clause: "Charter amendment mechanics",
        why: "An increase in authorised shares that is approved but never filed leaves the company unable to issue what it promised.",
      },
    ],
    cpgIssues: [
      "Authorising enough shares before a round is routine and routinely late; the crunch usually arrives when the round is funding a production deposit or a retailer authorization.",
      "Small early holders from launch parties and farmers markets are hard to reach years later, and every one of them may need to sign at exit.",
      "Adopting an equity plan needs shareholder approval, and founders usually discover this when they want to give options to a first sales hire before a season starts.",
      "A brand sale to a strategic acquirer needs shareholder approval and clean signature logistics, which is why cap-table hygiene is an exit issue and not an admin issue.",
      "Converting an LLC to a corporation ahead of an institutional round triggers owner-level approvals that have to be papered properly.",
    ],
    founderMistakes: [
      "Reaching a closing and finding there were not enough authorised shares to issue.",
      "Losing contact with early shareholders and spending weeks tracking them down during a sale process.",
      "Granting options under a plan that shareholders had never formally adopted.",
      "Approving an amendment but never filing it, so nothing actually changed.",
    ],
    negotiationPoints: [
      "Ask counsel to confirm the threshold and the notice steps before circulating anything for signature.",
      "Ask that contact details for every holder are verified annually so exit signatures are not a search project.",
      "Ask that authorised share capacity is reviewed before every financing conversation starts.",
      "Ask that plan adoptions and charter amendments are filed and confirmed, not just signed.",
    ],
    redFlags: [
      "A consent circulated without checking the required threshold.",
      "Signatures collected against an out-of-date cap table.",
      "Approvals of actions described only in general terms.",
      "No notice sent to shareholders who did not sign.",
    ],
    beforeYouSign: [
      "A current, verified cap table with contact details.",
      "The bylaws and certificate of incorporation, for thresholds and mechanics.",
      "The final form of whatever is being approved.",
    ],
    related: [
      "board-consent",
      "corporate-bylaws",
      "equity-incentive-plan",
      "shareholder-agreement",
      "cap-table-documentation",
      "voting-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "written-consent-of-members",
    name: "Written Consent of Members",
    category: "formation",
    subcategory: "Board & member actions",
    aliases: ["LLC Member Consent", "Member Written Consent", "Unanimous Consent of Members"],
    stages: ["pre-launch", "growth", "fundraising"],
    importance: "common",
    entityTypes: ["llc"],
    parties: "LLC + its Members",
    whenYouNeedIt:
      "When the operating agreement requires member approval and you do not want to hold a meeting: admitting a member, approving a capital call, appointing a manager, approving a major commitment or converting the entity.",
    whatItDoes:
      "Records member approval in writing, signed by the members whose votes the operating agreement requires for that specific action.",
    whyItMatters:
      "An LLC's approvals live entirely in its operating agreement, so the consent is the only proof that the required members actually agreed. In practice this matters when money is moving: a capital call to fund a packaging order, admitting the family member who wired cash for the first run, or converting to a corporation because an institutional investor requires it. Actions taken without the consent the agreement calls for get raised later by a lender, an investor or a buyer, and curing them means going back to members whose enthusiasm may have changed.",
    majorTerms: [
      "The action approved and the threshold required by the operating agreement",
      "Admission of a new member and the units issued",
      "Capital call approval and each member's share",
      "Appointment or removal of a manager",
      "Approval of commitments above the authority threshold",
      "Amendment of the operating agreement",
      "Conversion or reorganisation of the entity",
      "Distributions and any deviation from the standard allocation",
      "Signatures and effective date",
    ],
    clausesToWatch: [
      {
        clause: "Threshold required for this specific action",
        why: "Operating agreements use different thresholds for different actions, and using the wrong one leaves the approval open to challenge.",
      },
      {
        clause: "Admission of a new member",
        why: "Admitting someone without the required consent creates a disputed ownership claim that surfaces at the worst time.",
      },
      {
        clause: "Capital call approval",
        why: "If the consent does not track the operating agreement's procedure exactly, the consequence for not funding may not apply.",
      },
      {
        clause: "Amendment consents",
        why: "Amendments approved informally leave two versions of the deal in circulation, which is a real problem in diligence.",
      },
      {
        clause: "Description of the approved commitment",
        why: "A generic description may not cover the actual obligation signed, leaving the authority question open.",
      },
    ],
    cpgIssues: [
      "Capital calls in CPG are triggered by can, label and carton minimums and by co-packer deposits, so member consents cluster around production planning rather than board calendars.",
      "The family member who wired money for the first run is often admitted as a member informally; without a consent and a unit issuance, what they hold is a claim rather than a position.",
      "Commitments that exceed a manager's authority — a multi-year ingredient supply deal, a personal guarantee, a distribution exclusivity — need member approval and rarely get it in writing.",
      "Converting from an LLC to a corporation before an institutional raise requires member approvals, and it is usually attempted on a compressed timeline.",
      "A member who will not sign while a production slot is pending is a real operational risk, which is why thresholds and timelines should be set before the pressure arrives.",
    ],
    founderMistakes: [
      "Calling capital by group text and having no record when a member later denied agreeing.",
      "Treating a family investor as a member for years with no consent, no unit issuance and no ledger entry.",
      "Amending the profit split informally, leaving two conflicting versions of the deal.",
      "Signing an exclusivity commitment above the manager's authority with no member consent behind it.",
    ],
    negotiationPoints: [
      "Ask that every capital call is papered with a consent that follows the operating agreement's procedure exactly.",
      "Ask that admission of any new member is done by consent with a unit issuance and a ledger update.",
      "Ask for consent thresholds and response deadlines that fit a production planning cycle.",
      "Ask counsel to confirm which threshold applies before circulating anything.",
    ],
    redFlags: [
      "Approvals recorded only in messages or email threads.",
      "A consent that does not match the threshold in the operating agreement.",
      "New members treated as admitted with no unit issuance.",
      "Amendments circulating in more than one version.",
    ],
    beforeYouSign: [
      "The current operating agreement, including every amendment.",
      "The ownership schedule showing current units and members.",
      "The final form of the action being approved.",
    ],
    related: [
      "llc-operating-agreement",
      "multi-member-llc-operating-agreement",
      "single-member-llc-operating-agreement",
      "board-consent",
      "membership-interest-purchase-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "board-resolution",
    name: "Board Resolution",
    category: "formation",
    subcategory: "Board & member actions",
    aliases: ["Corporate Resolution", "Banking Resolution", "Resolution of the Board of Directors"],
    stages: ["pre-launch", "launch", "growth"],
    importance: "common",
    entityTypes: ["corporation"],
    parties: "Corporation (adopted by its Board)",
    whenYouNeedIt:
      "When an outside party wants proof that the board approved something specific — a bank opening an account, an insurer issuing a policy, a landlord signing a lease, a lender taking security.",
    whatItDoes:
      "Records a single board decision in a short standalone document that can be handed to a third party without disclosing the rest of the minute book.",
    whyItMatters:
      "A resolution is the piece of paper that unblocks an operational step: the account that receives distributor remittances, the equipment lease for a filling line, the officer authorised to sign a retailer vendor agreement. Founders discover they need one at the counter, and the delay is measured in days that a production schedule usually cannot absorb. It is also the standard tool for ratifying commitments a founder already signed without authority, which is common and fixable if done deliberately.",
    majorTerms: [
      "The specific action authorised",
      "The person authorised to act and the limits on that authority",
      "Accounts, institutions or counterparties named",
      "Any dollar cap on the authority granted",
      "Duration of the authority",
      "Ratification of prior related actions",
      "Certification by the secretary and the effective date",
    ],
    clausesToWatch: [
      {
        clause: "Breadth of the authority granted",
        why: "Banks and lessors often supply forms granting far wider authority than the transaction needs.",
      },
      {
        clause: "Named individuals versus titles",
        why: "Naming a person means the resolution goes stale when they leave; naming a title means whoever holds it can act.",
      },
      {
        clause: "Dollar limits",
        why: "Without a cap, an authorisation intended for one lease covers a much larger commitment later.",
      },
      {
        clause: "Ratification of prior acts",
        why: "Useful clean-up, but only when the prior acts are described specifically enough to actually be covered.",
      },
    ],
    cpgIssues: [
      "Banking resolutions govern the account that receives distributor and retailer remittances, which is also the account deductions come out of.",
      "Equipment lessors financing filling, labelling or cold-storage equipment usually require both a resolution and a personal guarantee.",
      "Retailer and 3PL onboarding packets ask for proof of signing authority, and mismatched entity names between the resolution, the insurance certificate and the vendor record stall the account.",
      "Cold-storage and warehouse leases signed under time pressure before a season often need a resolution the company has not prepared.",
      "Contracts a founder signed before any officer was appointed are commonly cleaned up by a ratifying resolution, which is worth doing before a raise rather than during one.",
    ],
    founderMistakes: [
      "Being unable to open the business bank account before a launch because no resolution existed.",
      "Signing a bank's standard resolution granting authority far beyond the account being opened.",
      "Naming an individual who later left, so a stale resolution blocked a payment run.",
      "Never ratifying the contracts signed in the first year, leaving authority questions for diligence to find.",
    ],
    negotiationPoints: [
      "Ask to narrow any third-party form resolution to the transaction actually being approved.",
      "Ask that authority is granted by title where possible, with a dollar cap.",
      "Ask for a ratifying resolution covering early contracts, prepared with counsel before your first raise.",
    ],
    redFlags: [
      "A form resolution granting unlimited authority to one person.",
      "Authority with no expiry and no cap.",
      "A resolution naming an entity whose name does not exactly match the charter.",
      "Ratification language that describes nothing specific.",
    ],
    beforeYouSign: [
      "The bylaws, to confirm who must approve and how.",
      "The underlying agreement the resolution authorises, in final form.",
    ],
    related: [
      "board-consent",
      "officer-appointment-resolution",
      "corporate-bylaws",
      "indemnification-agreement",
      "personal-guarantee",
    ],
    attorneyReview: "depends",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "officer-appointment-resolution",
    name: "Officer Appointment Resolution",
    category: "formation",
    subcategory: "Board & member actions",
    aliases: ["Appointment of Officers", "Officer Election Resolution", "Incumbency Resolution"],
    stages: ["idea", "pre-launch", "launch"],
    importance: "common",
    entityTypes: ["corporation"],
    parties: "Corporation (adopted by its Board)",
    whenYouNeedIt:
      "At incorporation, and again whenever titles change — a co-founder becomes CEO, someone is appointed secretary, a new head of sales is given signing authority.",
    whatItDoes:
      "Appoints named people to officer positions and states what each of them is authorised to sign on the company's behalf.",
    whyItMatters:
      "Counterparties rely on titles. When someone described as VP of Sales signs a territory agreement with a distributor, the distributor is not going to check your minute book — it will treat the deal as done. This resolution is how you decide in advance who can create that kind of commitment and up to what value, and it is the document a bank, an insurer or a retailer's vendor team asks for when they want to confirm the signature on their paperwork.",
    majorTerms: [
      "Officer positions and the individuals appointed",
      "Signing authority by subject matter",
      "Dollar thresholds requiring board approval",
      "Term of appointment and removal",
      "Who may bind the company for banking and insurance",
      "Delegation of authority to employees",
      "Secretary's role in maintaining the record",
    ],
    clausesToWatch: [
      {
        clause: "Signing thresholds",
        why: "Without a stated cap, a sales title carries authority to commit the company to obligations well beyond its size.",
      },
      {
        clause: "Delegation of authority",
        why: "Broad delegation lets someone further down the organisation bind the company with no visibility.",
      },
      {
        clause: "Removal and effective dates",
        why: "An officer who left but was never removed on paper can still appear authorised to a counterparty.",
      },
      {
        clause: "Overlap with the bylaws",
        why: "Appointments inconsistent with the bylaws create doubt about whether the officer was validly appointed at all.",
      },
    ],
    cpgIssues: [
      "Distributor and broker agreements are frequently signed by whoever attended the meeting, and the title on the signature block is what the counterparty relies on.",
      "Retailer vendor portals require an officer name and title, which must match the entity record, the insurance certificate and the banking authority.",
      "Co-packer master agreements and quality agreements carry indemnities and guarantees, so who may sign them should be a deliberate decision.",
      "Certification bodies and facility registrations list responsible officers, so title changes have downstream administrative consequences.",
      "Founders who use CEO on a business card without ever being appointed create an authority gap that a buyer's counsel will find and ask about.",
    ],
    founderMistakes: [
      "Signing a distributor agreement as CEO when no board action ever appointed a CEO.",
      "Letting a sales lead with a VP title commit to an exclusive territory nobody approved.",
      "Never removing a departed officer on paper, leaving them apparently authorised.",
      "Using titles inconsistently across contracts, the bank, insurance and the retailer vendor record.",
    ],
    negotiationPoints: [
      "Ask for signing authority described by subject matter and by dollar threshold rather than by title alone.",
      "Ask that exclusivity, guarantees, multi-year terms and new debt always require board approval regardless of who signs.",
      "Ask that officer changes are recorded and communicated to the bank, the insurer and key vendors.",
    ],
    redFlags: [
      "Officer titles used externally with no appointment in the record.",
      "No dollar threshold on anyone's authority.",
      "Delegation language with no limits.",
      "Inconsistent entity names or titles between the resolution and vendor records.",
    ],
    beforeYouSign: [
      "The bylaws, for the officer positions the company is supposed to have.",
      "A list of the agreements each officer will realistically be asked to sign.",
    ],
    related: [
      "board-consent",
      "board-resolution",
      "corporate-bylaws",
      "indemnification-agreement",
      "independent-director-agreement",
    ],
    attorneyReview: "depends",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "founder-ip-assignment",
    name: "IP Assignment from Founder to Company",
    category: "formation",
    subcategory: "Founder IP",
    aliases: ["Founder IP Transfer", "Technology Assignment Agreement", "Contribution and Assignment Agreement"],
    stages: ["idea", "development", "pre-launch"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Founder + Company",
    whenYouNeedIt:
      "In the first days after forming the entity, and again any time you discover an asset the brand relies on that is still in a founder's personal name.",
    whatItDoes:
      "Transfers everything the founder created or acquired for the brand before or outside the company into the company, listed asset by asset: recipes, formulation notes, the brand name, trademark filings, artwork, photography, domains and accounts.",
    whyItMatters:
      "In a CPG brand almost every valuable asset starts in a founder's personal hands — the recipe developed at home, the name registered on a personal card, the logo a freelancer was paid for out of a personal account. None of it moves into the company automatically, and a co-packer will produce a formula the company does not own without ever asking. At an acquisition the buyer is purchasing exactly these assets, so unassigned founder IP becomes an indemnity, an escrow or a lower price. It costs almost nothing to fix in month one.",
    majorTerms: [
      "Schedule of assigned assets, listed individually",
      "Recipes, formulations, process notes and specification files",
      "Brand names, trademark applications and registrations",
      "Logos, packaging artwork and source design files",
      "Product photography, video and website content",
      "Domains, social handles, seller and ad accounts",
      "Customer and wholesale contact lists",
      "Further assurances obligation to sign whatever else is needed",
      "Consideration for the assignment",
      "Any assets expressly excluded",
    ],
    clausesToWatch: [
      {
        clause: "The schedule of assets",
        why: "A general assignment with an empty or generic schedule is what fails in diligence, because nobody can tell what actually moved.",
      },
      {
        clause: "Trademark filings",
        why: "A mark filed in a personal name usually needs its own properly handled assignment rather than a sentence in a general agreement.",
      },
      {
        clause: "Excluded assets",
        why: "An exclusion for other recipes or a side project can quietly carve out something the brand actually depends on.",
      },
      {
        clause: "Further assurances",
        why: "Without an obligation to sign follow-up paperwork later, a departed founder can simply stop responding when a filing needs their signature.",
      },
      {
        clause: "Assets the founder never owned",
        why: "A founder cannot assign artwork a freelancer still owns, so the chain has to be repaired at the freelancer before it can move here.",
      },
    ],
    cpgIssues: [
      "The formula file matters more than the recipe card: iterations, bench notes, scale-up adjustments made with the co-packer, and the specification sheet all need to sit with the company.",
      "The brand name is often registered as a domain and filed as a trademark by a founder personally months before the entity exists.",
      "Packaging artwork paid for personally via a freelancer marketplace or a friend usually has no assignment at all, so the founder is assigning something they may not hold.",
      "Product photography used on the label, the website and in retailer listings is licensed content unless it was properly assigned by the photographer.",
      "Amazon Seller Central, Shopify, Meta business accounts, the GS1 company prefix and the UPCs assigned under it are all held by a named party and do not move by themselves.",
      "Formula documents shared with a flavor house or a co-packer under one founder's personal email are hard to claim as company records later.",
    ],
    founderMistakes: [
      "Signing a one-line assignment of all intellectual property and never listing the trademark application filed six months earlier in a personal name.",
      "Paying a freelancer through an app for the logo with no assignment, then purporting to assign it to the company.",
      "Registering the domain on a personal account and losing access to it after a falling-out.",
      "Letting a departing co-founder keep the only copy of the formulation spreadsheet.",
      "Discovering at diligence that the GS1 prefix behind every UPC was licensed to a founder personally.",
    ],
    negotiationPoints: [
      "Ask that the schedule lists every asset by name, including filing numbers for any trademark applications.",
      "Ask counsel to handle trademark assignments properly and separately rather than relying on general language.",
      "Ask for a further assurances clause that survives the founder leaving.",
      "Ask that every freelancer and agency in the chain has signed an assignment before the founder assigns to the company.",
      "Ask that accounts and platform logins are transferred to company-controlled email addresses as part of the same exercise.",
    ],
    redFlags: [
      "An assignment with a blank or generic schedule.",
      "Exclusions covering recipes or brand names in the same category.",
      "No further assurances obligation.",
      "A founder unwilling to assign the brand name or the formula.",
      "Assets in the schedule the founder cannot show they own.",
    ],
    beforeYouSign: [
      "A complete asset inventory: every recipe file, artwork file, photo library, domain, handle, account and filing, with the name it is currently under.",
      "Evidence of assignment from every freelancer, designer, photographer and agency in the chain.",
      "Trademark filing details, including applicant name and status.",
    ],
    related: [
      "founder-invention-assignment",
      "piia",
      "ip-assignment-agreement",
      "trademark-assignment",
      "work-for-hire-agreement",
      "formula-ownership-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "founder-invention-assignment",
    name: "Founder Invention Assignment Agreement",
    category: "formation",
    subcategory: "Founder IP",
    aliases: ["Invention Assignment Agreement", "Founder Inventions Agreement", "Confidential Information and Invention Assignment"],
    stages: ["idea", "development", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Founder + Company",
    whenYouNeedIt:
      "At formation, signed by every founder alongside their equity documents, and before any new formulation work begins.",
    whatItDoes:
      "Assigns to the company everything a founder creates going forward while working on the business, requires disclosure of inventions they made before joining, and records anything they are expressly keeping.",
    whyItMatters:
      "The founder IP assignment handles what already exists; this one handles what comes next — the line extension developed at home, the process change worked out on the co-packer's line, the new flavor tested at a friend's kitchen. Without it, ownership of each new product is a fresh conversation, and a founder who leaves mid-development can claim the work that was in progress. The prior inventions schedule is the other half: it is where a founder writes down what they are not giving the company, and it deserves careful reading rather than a signature.",
    majorTerms: [
      "Present assignment of inventions created during the relationship",
      "Scope: what counts as within the business",
      "Prior inventions disclosure schedule",
      "Excluded inventions the founder retains",
      "Disclosure obligation for new inventions",
      "Assistance with filings and registrations",
      "Confidentiality obligations",
      "Return of materials on departure",
      "Survival after the founder leaves",
    ],
    clausesToWatch: [
      {
        clause: "Prior inventions schedule",
        why: "Anything listed there is being kept by the founder, and a recipe listed as prior work can sit outside the company forever.",
      },
      {
        clause: "Scope of assigned inventions",
        why: "Too narrow and adjacent work escapes; too broad and a founder's unrelated projects get swept in, which they will resist.",
      },
      {
        clause: "Duty to assist with filings",
        why: "Registrations often need the inventor's signature years later, and a departed founder with no obligation may not provide it.",
      },
      {
        clause: "Return of materials",
        why: "Formula files, cost models and specification sheets leaving with a founder is a practical loss even when ownership is clear.",
      },
      {
        clause: "Survival of obligations",
        why: "If the assignment stops at departure, work in progress at that moment becomes contested exactly when it matters.",
      },
    ],
    cpgIssues: [
      "Line extensions and new flavors developed at home, at a friend's kitchen or during a plant trial are inventions in progress, and ownership should not depend on where the work happened.",
      "Process improvements worked out jointly on a co-packer's line raise a genuine question about who owns them, which is why the co-packer agreement and this one need to say the same thing.",
      "A founder who still works at an established food or beverage company may be subject to their employer's own agreement, and that is a question to resolve with counsel before the brand is built on the work.",
      "Formulation notebooks, bench sheets, cost models and supplier specifications are the practical embodiment of the IP, and the return-of-materials clause is what gets them back.",
      "Sensory and consumer test results, shelf-life data and stability results are assets a buyer will ask for, and they need to belong to the company rather than to whoever ran the trial.",
    ],
    founderMistakes: [
      "Signing with a blank prior inventions schedule, then later claiming a recipe was pre-existing and outside the company.",
      "Never asking a founder with a food industry day job whether their employer agreement could reach nights-and-weekends work.",
      "Losing the entire formulation history when a founder left with the only notebook.",
      "Assuming a plant trial improvement belonged to the brand when the co-packer agreement said something different.",
    ],
    negotiationPoints: [
      "Ask every founder to complete the prior inventions schedule properly, and read what they list.",
      "Ask that the scope covers the product categories the brand actually plans to enter, described concretely.",
      "Ask for a duty to assist with filings that survives departure.",
      "Ask that the return-of-materials obligation names the specific files: formulations, specifications, cost models, test results.",
      "Ask counsel to review any founder's existing employer agreement before the brand relies on their work.",
    ],
    redFlags: [
      "A prior inventions schedule listing a recipe or brand name central to the business.",
      "No disclosure obligation for new inventions.",
      "No survival after departure.",
      "A founder unwilling to disclose an existing employer IP agreement.",
    ],
    beforeYouSign: [
      "Each founder's completed prior inventions schedule.",
      "Any existing employment or consulting agreement a founder is still bound by.",
      "The co-packer and development agreements, to confirm they treat process improvements consistently.",
    ],
    related: [
      "founder-ip-assignment",
      "piia",
      "product-development-ip-agreement",
      "formula-ownership-agreement",
      "trade-secret-protection-agreement",
      "ip-assignment-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "piia",
    name: "Proprietary Information and Inventions Assignment Agreement",
    category: "formation",
    subcategory: "Founder IP",
    aliases: ["PIIA", "CIIAA", "Confidential Information and Invention Assignment Agreement", "Employee IP Agreement"],
    stages: ["development", "launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Company + Employee, Founder or Contractor",
    whenYouNeedIt:
      "Signed by everyone before their first day: founders, employees, and anyone else who will see the formula, the cost model or the account list.",
    whatItDoes:
      "Combines confidentiality, assignment of work created for the company, a return-of-materials obligation and usually a non-solicit into the standard agreement every person signs on joining.",
    whyItMatters:
      "A formula is only protectable as a secret if the company treats it as one, and this agreement is the visible part of doing that. In practice the exposure is concrete: your operations hire knows every co-packer cost, every ingredient supplier and every margin by channel, and the industry that would value that information is small and hiring. Without a signed agreement, what they take with them is a commercial problem you have no paperwork to address, and a buyer will notice which of your people never signed one.",
    majorTerms: [
      "Definition of confidential information",
      "Assignment of work product and inventions",
      "Prior inventions disclosure",
      "Return of materials and devices on departure",
      "Non-solicitation of employees and, where used, customers",
      "Obligations to third-party confidential information",
      "Duration of confidentiality obligations",
      "Assistance with registrations",
      "Notice obligations about competing activities",
    ],
    clausesToWatch: [
      {
        clause: "Definition of confidential information",
        why: "If it does not clearly cover formulas, cost sheets, supplier terms and account-level velocity, the most valuable information may not be protected.",
      },
      {
        clause: "Contractor coverage",
        why: "An employee form used with an agency or a lab often fails to move ownership the way a properly drafted contractor agreement would.",
      },
      {
        clause: "Return of materials",
        why: "Recipes, cost models and buyer contact lists live on personal phones and drives, and only this clause requires them back.",
      },
      {
        clause: "Non-solicit scope",
        why: "Overbroad restrictions invite disputes, and enforceability varies by state, which is a question for counsel rather than a template.",
      },
      {
        clause: "Third-party information",
        why: "A hire arriving from a larger food company can bring information they should not, which creates exposure for you.",
      },
    ],
    cpgIssues: [
      "Trade secret protection depends on actually restricting access: who holds the master formula, whether it is marked, and whether the co-packer and the lab have signed their own agreements.",
      "Demo staff, brand ambassadors and part-time merchandisers see pricing, promotional calendars and account lists, and are the group most often onboarded with no paperwork.",
      "An operations hire knows landed cost, co-packer rates, yield and ingredient sourcing — the exact package a competitor would value.",
      "Food scientists, flavor houses, design agencies and photographers need their own agreements; an employee form is the wrong instrument for a lab or a studio.",
      "Retailer and distributor data — deductions, velocity, promotional performance — is often contractually confidential to you, so your people need to be bound to protect it.",
    ],
    founderMistakes: [
      "Having the co-packer sign an NDA while the food scientist who developed the formula signed nothing.",
      "Onboarding a sales lead with an offer letter alone and no confidentiality or assignment agreement.",
      "Using the employee form with a design agency and assuming the packaging artwork transferred.",
      "Never collecting materials back from a departing operations hire who held the entire supplier file.",
    ],
    negotiationPoints: [
      "Ask that confidential information expressly names formulas, specifications, cost sheets, supplier terms, customer lists and velocity data.",
      "Ask for a return-of-materials obligation that covers personal devices and cloud accounts.",
      "Ask counsel which restrictive covenants are appropriate for the states where your people actually work.",
      "Ask that contractors, labs and agencies get their own agreements rather than the employee form.",
    ],
    redFlags: [
      "Anyone with access to the formula who has not signed.",
      "A confidentiality definition that does not mention formulations or cost information.",
      "No return-of-materials obligation.",
      "One form used indiscriminately for employees, contractors and agencies.",
    ],
    beforeYouSign: [
      "A list of everyone with access to the formula, the cost model and the account list, and whether each has signed.",
      "The states where your team members are located, so counsel can advise on the restrictive covenants.",
    ],
    related: [
      "founder-invention-assignment",
      "employee-ip-assignment-agreement",
      "employee-confidentiality-agreement",
      "contractor-work-for-hire-agreement",
      "trade-secret-protection-agreement",
      "formula-ownership-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "vesting-agreement",
    name: "Vesting Agreement",
    category: "formation",
    subcategory: "Vesting & equity plans",
    aliases: ["Equity Vesting Agreement", "Vesting Schedule Agreement", "Unit Vesting Agreement"],
    stages: ["idea", "pre-launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Equity Holder",
    whenYouNeedIt:
      "Whenever equity is being given to someone for work they have not done yet — a founder, an early hire, a part-time operator, an advisor.",
    whatItDoes:
      "Ties ownership to time or to milestones: the holder earns their equity as they work, and the company keeps a defined right over the portion not yet earned.",
    whyItMatters:
      "Equity handed over in full on day one is the most expensive mistake an early CPG company makes, because the people who leave first are usually the ones who joined for the excitement of a launch rather than the grind of a second production run. Vesting converts a promise into a schedule, which is what lets you replace someone without giving away a second block of the company to do it. Investors and acquirers treat the absence of vesting as a signal about how the rest of the paperwork was handled.",
    majorTerms: [
      "Vesting period, cliff and frequency of vesting",
      "Start date and any credit for prior work",
      "Milestone conditions, where used",
      "Treatment on voluntary and involuntary departure",
      "Acceleration on a sale of the company",
      "Forfeiture or repurchase of unvested equity",
      "Effect of a leave of absence or reduced hours",
      "Whether unvested equity carries voting or distribution rights",
    ],
    clausesToWatch: [
      {
        clause: "Milestone definitions",
        why: "A milestone like a national listing sounds clear until you argue about whether a regional test counts.",
      },
      {
        clause: "Cliff length",
        why: "A cliff that spans only one selling season tells you very little about whether the person will actually deliver.",
      },
      {
        clause: "Acceleration triggers",
        why: "Full acceleration on any sale reduces what an acquirer will pay for a team it wanted to retain through transition.",
      },
      {
        clause: "Reduced hours and leave",
        why: "Founders often go part-time when cash runs tight, and silence here means they keep vesting at full speed.",
      },
      {
        clause: "Forfeiture mechanics",
        why: "If nothing says how unvested equity comes back, the schedule is a statement of intent rather than a right.",
      },
    ],
    cpgIssues: [
      "Milestone vesting is tempting in CPG because the milestones feel objective — a first chain authorization, a full truckload order, a completed co-packer qualification — but each needs a precise written definition or it becomes a dispute.",
      "Category seasonality means a one-year cliff may cover only a single selling season, which is a thin basis for handing over a permanent stake.",
      "Acceleration matters more here than in most industries because brand acquisitions usually depend on the founding team staying through transition.",
      "Early CPG contributors are often part time around another job, so vesting should say what happens when someone's hours drop.",
      "Equity for a single distributor or buyer introduction should vest against the actual relationship rather than against the calendar.",
    ],
    founderMistakes: [
      "Granting a full stake to an early operations partner with no vesting, then losing them after the first production run.",
      "Writing a milestone as first major retailer without defining what major meant, and arguing about it for months.",
      "Letting a founder drop to part time while continuing to vest at the original rate.",
      "Using a schedule with no forfeiture mechanism, so unvested equity never actually came back.",
    ],
    negotiationPoints: [
      "Ask for a cliff long enough to span more than one selling season.",
      "Ask that every milestone is defined in writing with the account, the volume or the certification named.",
      "Ask for a stated consequence when someone reduces hours or takes extended leave.",
      "Ask that acceleration is tied to a termination following a sale rather than the sale alone.",
      "Ask for a clear forfeiture or repurchase mechanism, with the price stated.",
    ],
    redFlags: [
      "Equity granted outright with no schedule to anyone still expected to work.",
      "Milestones described in adjectives rather than in named accounts or volumes.",
      "No treatment of departure at all.",
      "Full single-trigger acceleration across the board.",
    ],
    beforeYouSign: [
      "The current cap table and the fully diluted position after this grant.",
      "A written definition of every milestone the schedule relies on.",
    ],
    related: [
      "founder-vesting-agreement",
      "restricted-stock-purchase-agreement",
      "equity-incentive-plan",
      "stock-option-agreement",
      "advisor-equity-agreement",
      "co-founder-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "founder-vesting-agreement",
    name: "Founder Vesting / Reverse Vesting Agreement",
    category: "formation",
    subcategory: "Vesting & equity plans",
    aliases: ["Reverse Vesting Agreement", "Founder Stock Restriction Agreement", "Founder Repurchase Agreement"],
    stages: ["idea", "pre-launch", "fundraising"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Company + Founders",
    whenYouNeedIt:
      "At formation, or the moment you realise the founders hold their equity outright with nothing tying it to staying.",
    whatItDoes:
      "Leaves founder equity issued and outstanding but subject to a company repurchase right that lapses over time, so a founder who leaves early gives back the portion they had not yet earned.",
    whyItMatters:
      "A co-founder holding a third of the company who stopped working in month five is the most common reason an otherwise strong CPG brand becomes unfundable — the next investor is being asked to fund a business where a large slice of the upside belongs to someone who is not in it. In this category the founding period is long: formulation, shelf-life work and co-packer qualification can run a year before a single case ships, so a departure at month five may come before any revenue exists at all. Reverse vesting is what turns that from a crisis into a paperwork exercise.",
    majorTerms: [
      "Repurchase right over unvested shares or units and its price",
      "Vesting schedule, cliff and start date",
      "Credit for pre-formation work",
      "Treatment on departure, with and without cause",
      "Acceleration on a sale or on termination after a sale",
      "Voting and distribution rights on unvested equity",
      "Transfer restrictions during the restricted period",
      "Spousal consent, where relevant",
      "Board approval of the arrangement",
    ],
    clausesToWatch: [
      {
        clause: "Vesting commencement date",
        why: "A start date at incorporation ignores the year a founder spent formulating, which is real work in this category.",
      },
      {
        clause: "Repurchase price",
        why: "A right to repurchase at fair value is not usable by a company with no cash; at original cost it is.",
      },
      {
        clause: "Definition of cause and of good reason",
        why: "These two definitions decide who keeps a large block of equity in a contested exit, and vague versions guarantee a fight.",
      },
      {
        clause: "Acceleration structure",
        why: "Single-trigger acceleration can make an acquirer nervous about retention and reduce what they will pay.",
      },
      {
        clause: "Personal guarantees given by the departing founder",
        why: "A founder who gives up unvested equity while still guaranteeing a co-packer line carries exposure with no remaining upside.",
      },
    ],
    cpgIssues: [
      "A four-year schedule starting at incorporation can be measured from the wrong date when the product took eighteen months to formulate and qualify before the entity existed.",
      "Departure risk peaks around the first real production run, when the work turns from tasting sessions into pallets, deductions and 6 a.m. plant calls.",
      "The departing founder often holds the co-packer relationship or the buyer relationships, so the vesting conversation and the handover conversation belong together.",
      "Personal guarantees on co-packer credit, equipment and warehouse space usually sit with one founder and need to be addressed when their equity position changes.",
      "Acquirers of brands generally want founders to stay through a transition, which is why acceleration terms are read closely at exit.",
    ],
    founderMistakes: [
      "Skipping vesting between trusted co-founders and having no mechanism when one left before the first production run.",
      "Setting a repurchase price at fair value, so the company could never afford to exercise the right.",
      "Ignoring two years of pre-formation formulation work when setting the start date.",
      "Missing the short filing windows that follow putting shares under a repurchase right, because nobody raised them at signing.",
    ],
    negotiationPoints: [
      "Ask that every founder is subject to the same arrangement, including whoever is proposing it.",
      "Ask for a start date that credits documented pre-formation work, with that work described.",
      "Ask that the repurchase price is the original purchase price so the right can actually be used.",
      "Ask for written cause and good reason definitions rather than general standards.",
      "Ask counsel and a tax adviser at signing what steps and deadlines follow from putting equity under a repurchase right.",
    ],
    redFlags: [
      "One founder exempt from vesting.",
      "No repurchase mechanism, only a statement that equity vests.",
      "Cause defined so broadly that any departure forfeits everything.",
      "No board approval and no ledger or ownership schedule update.",
    ],
    beforeYouSign: [
      "The current cap table and each founder's holding.",
      "A written record of what each founder did before formation, if you are crediting it.",
      "A list of personal guarantees each founder has given.",
    ],
    related: [
      "founder-stock-purchase-agreement",
      "restricted-stock-purchase-agreement",
      "vesting-agreement",
      "co-founder-agreement",
      "founder-ip-assignment",
      "cap-table-documentation",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Putting founder equity under a repurchase schedule has both securities and tax consequences, and there are time-limited elections tied to the date the restriction is imposed whose windows are short and routinely missed — involve counsel and a tax adviser at signing.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "equity-incentive-plan",
    name: "Equity Incentive Plan",
    category: "formation",
    subcategory: "Vesting & equity plans",
    aliases: ["Stock Option Plan", "Stock Incentive Plan", "Option Pool"],
    stages: ["growth", "fundraising"],
    importance: "common",
    entityTypes: ["corporation"],
    parties: "Corporation (adopted by the board and approved by shareholders)",
    whenYouNeedIt:
      "Before you promise equity to anyone who is not a founder — a first sales hire, an operations lead, a long-term contractor — and generally before or during your first priced round.",
    whatItDoes:
      "Creates a pool of shares reserved for grants, sets who administers it, what kinds of awards can be made, and the rules every individual grant then hangs from.",
    whyItMatters:
      "The plan is what makes a promise of equity real; without one, an offer letter mentioning options is a commitment the company cannot yet deliver. Pool size is also a live negotiation in any financing, because investors expect the pool to be sized before the round and the dilution to land on the existing holders. Founders who size it casually give away more than they intended, and founders who have no plan at all end up making verbal promises that surface at diligence as undocumented claims.",
    majorTerms: [
      "Number of shares reserved for the pool",
      "Types of awards permitted",
      "Who administers the plan and grants awards",
      "Eligibility, including employees, contractors and advisors",
      "Standard vesting terms and cliff",
      "Exercise price setting and valuation process",
      "Post-termination exercise periods",
      "Treatment on a change of control",
      "Transfer restrictions and repurchase rights",
      "Board and shareholder approval requirements",
      "Plan term and amendment rights",
    ],
    clausesToWatch: [
      {
        clause: "Pool size",
        why: "Sized before a round it dilutes the founders; sized after, it dilutes everyone — investors will expect the first, so know what you are agreeing to.",
      },
      {
        clause: "Change of control treatment",
        why: "Automatic full acceleration can complicate a brand sale where the acquirer wants the team to stay through transition.",
      },
      {
        clause: "Post-termination exercise window",
        why: "A short window means departing employees usually forfeit, which is a retention and reputation issue as much as a legal one.",
      },
      {
        clause: "Exercise price and valuation",
        why: "Setting prices without a defensible valuation process creates problems that surface years later during diligence.",
      },
      {
        clause: "Eligibility of non-employees",
        why: "Grants to contractors, brokers and advisors raise different questions from employee grants and need to be permitted deliberately.",
      },
      {
        clause: "Administration and delegation",
        why: "If it is unclear who can grant, verbal promises get made by people who cannot actually make them.",
      },
    ],
    cpgIssues: [
      "The first equity hires in a CPG company are usually a sales lead and an operations lead, and both will be recruited against offers from larger food companies with real benefits.",
      "Part-time and hourly staff — demo leads, merchandisers, warehouse help — are people founders often want to include, and the plan has to permit that structure.",
      "A broker, distributor contact or co-packer relationship asking for equity instead of fees is a different conversation from an employee grant, with disclosure consequences at your next raise.",
      "Investors size the pool as part of pricing the round, and a pool created at the last minute usually lands entirely on the founders.",
      "At a brand acquisition the whole pool has to be unwound cleanly, so undocumented verbal promises become a deduction from the purchase price.",
    ],
    founderMistakes: [
      "Promising options in an offer letter months before any plan existed.",
      "Agreeing a pool size during a financing without modelling the dilution it created for the founders.",
      "Granting to a contractor under an employee-only plan.",
      "Setting exercise prices with no valuation basis and having to explain it during diligence.",
    ],
    negotiationPoints: [
      "Ask for a pool sized against a real hiring plan for the next 18 months rather than a round number.",
      "Ask whether change of control treatment should be double trigger rather than automatic.",
      "Ask for a post-termination exercise window you would be comfortable defending to a departing employee.",
      "Ask that the plan expressly permits the categories of people you actually intend to grant to.",
      "Ask counsel how the valuation for exercise pricing should be established before the first grant.",
    ],
    redFlags: [
      "Grants being made before the plan is adopted and approved.",
      "No valuation process for setting exercise prices.",
      "Unlimited delegation of grant authority.",
      "A pool number agreed in a term sheet with no hiring plan behind it.",
    ],
    beforeYouSign: [
      "A hiring plan showing the roles and the equity each is expected to need.",
      "A cap table model showing dilution from the proposed pool.",
      "Board and shareholder approvals prepared alongside the plan.",
    ],
    related: [
      "stock-option-agreement",
      "board-consent",
      "written-consent-of-shareholders",
      "restricted-stock-purchase-agreement",
      "advisor-equity-agreement",
      "cap-table-documentation",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "An equity plan sits on top of securities and tax rules that differ by award type and by recipient, and several of the deadlines that follow a grant are short and easy to miss — have counsel and a tax adviser set the plan up before the first promise is made.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "stock-option-agreement",
    name: "Stock Option Agreement",
    category: "formation",
    subcategory: "Vesting & equity plans",
    aliases: ["Option Grant Agreement", "Notice of Stock Option Grant", "Employee Option Agreement"],
    stages: ["growth", "fundraising"],
    importance: "common",
    entityTypes: ["corporation"],
    parties: "Corporation + Option Holder",
    whenYouNeedIt:
      "Each time you actually grant options under your plan — at hire, on a promotion, or when a contractor converts to a longer-term role.",
    whatItDoes:
      "Documents one grant: how many shares, at what exercise price, on what vesting schedule, for how long the option lasts, and what happens when the holder leaves or the company is sold.",
    whyItMatters:
      "Options are the main way a small brand competes for people who could earn more at an established food company, and the terms in this one-page grant decide whether that pitch is honest. The exercise window on departure and the change of control treatment are the two terms holders care about most and founders explain least. Grants promised verbally, priced without a valuation, or made before the plan existed all turn up as findings in the diligence that precedes a raise or a sale.",
    majorTerms: [
      "Number of shares under option and the class",
      "Exercise price and how it was set",
      "Vesting schedule, cliff and commencement date",
      "Award type and its conditions",
      "Expiration date of the option",
      "Post-termination exercise period",
      "Treatment on a change of control",
      "Early exercise rights, if permitted",
      "Transfer restrictions and company repurchase rights",
      "Reference to the governing plan",
    ],
    clausesToWatch: [
      {
        clause: "Post-termination exercise period",
        why: "A short window forces a departing employee to fund an exercise quickly or lose everything they earned.",
      },
      {
        clause: "Change of control treatment",
        why: "Whether options accelerate, convert or lapse in a brand sale is the term that decides what the holder actually receives.",
      },
      {
        clause: "Exercise price basis",
        why: "A price set without a defensible valuation creates a problem for both the company and the holder that surfaces later.",
      },
      {
        clause: "Vesting commencement date",
        why: "Grants made months after a start date without back-dating the schedule quietly shorten what the holder was promised.",
      },
      {
        clause: "Repurchase rights on exercised shares",
        why: "A repurchase right that survives exercise can take back shares the holder believed were fully theirs.",
      },
    ],
    cpgIssues: [
      "A brand sale can arrive in year two or three, so a four-year schedule means most of a sales hire's grant is unvested when the outcome happens — say so honestly at hire.",
      "Departing employees in an early CPG company rarely have cash to exercise, so a short exercise window in practice means forfeiture.",
      "Grants to brokers, demo agencies or a co-packer contact are non-employee grants and raise different questions that need counsel before the promise is made.",
      "Options promised in an offer letter before a plan and a pool exist are a recurring problem when the first real hire arrives ahead of the first financing.",
      "Acquirers model the option pool into the price of a brand, so grants that are undocumented or inconsistently priced come out of the founders' proceeds.",
    ],
    founderMistakes: [
      "Telling a first sales hire they had one percent, and issuing nothing for a year.",
      "Setting an exercise price by guess and having to explain it during diligence.",
      "Granting under a plan that had not been approved by shareholders.",
      "Never explaining the exercise window, so a departing employee discovered it after they left.",
    ],
    negotiationPoints: [
      "Ask that vesting commences on the actual start date, not the date the paperwork caught up.",
      "Ask whether an extended post-termination exercise period is appropriate for your team.",
      "Ask for change of control treatment written in plain language in the grant itself.",
      "Ask counsel how non-employee grants should be structured before promising equity to a broker or agency.",
    ],
    redFlags: [
      "Grants made before the plan exists or before the pool has capacity.",
      "No stated exercise price basis.",
      "Repurchase rights over exercised shares with no time limit.",
      "Terms in the grant that contradict the governing plan.",
    ],
    beforeYouSign: [
      "The governing plan document and the remaining pool capacity.",
      "The board approval for this specific grant.",
      "A current cap table showing the fully diluted position.",
    ],
    related: [
      "equity-incentive-plan",
      "board-consent",
      "restricted-stock-purchase-agreement",
      "advisor-equity-agreement",
      "offer-letter",
      "cap-table-documentation",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Option grants carry securities and tax consequences that differ by award type and recipient, and several deadlines that follow a grant or an exercise are short and easy to miss — have counsel and a tax adviser set the form and the pricing basis before the first grant.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "advisor-equity-agreement",
    name: "Advisor Equity Agreement",
    category: "formation",
    subcategory: "Advisors & directors",
    aliases: ["Advisor Agreement with Equity", "Advisor Grant Agreement", "Equity Advisor Agreement"],
    stages: ["idea", "pre-launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Advisor",
    whenYouNeedIt:
      "When someone with category experience or relationships is going to help regularly and wants equity rather than cash — usually the person who can open a door you cannot.",
    whatItDoes:
      "Sets what the advisor will actually do, over what period, in exchange for a defined equity grant that vests, with confidentiality and IP terms attached.",
    whyItMatters:
      "The CPG version of this deal is nearly always about access: someone introduces you to a co-packer with capacity, a regional distributor, or a buyer at a chain. An introduction is worth paying for, but paying for it with equity that never stops vesting turns one phone call into a permanent line on your cap table and a signature you will need at exit. Vesting, a term, and a written description of what the advisor is actually going to do are what keep the trade proportionate.",
    majorTerms: [
      "Equity amount and the form the grant takes",
      "Vesting schedule, usually monthly with a short cliff",
      "Defined deliverables: introductions, hours per month, specific accounts or categories",
      "Term of the engagement and how either side ends it",
      "What happens to unvested equity on termination",
      "Confidentiality obligations",
      "IP assignment for anything the advisor creates",
      "Conflicts and exclusivity within your category",
      "Expense reimbursement",
      "No authority to bind the company",
    ],
    clausesToWatch: [
      {
        clause: "Deliverables",
        why: "Advisor equity described only as advice vests whether or not anything happens, which is how introductions become annuities.",
      },
      {
        clause: "Category conflicts",
        why: "The same advisor may sit with three brands in your set, so what they learn about your cost and velocity travels.",
      },
      {
        clause: "Termination and unvested equity",
        why: "Without a clean end and forfeiture of the unvested portion, a disengaged advisor keeps accruing ownership.",
      },
      {
        clause: "Vesting cliff",
        why: "A long cliff on a short engagement means nothing vests; no cliff at all means a month of contact earns a stake.",
      },
      {
        clause: "No authority to bind",
        why: "Advisors who negotiate with distributors or co-packers on your behalf can create commitments you did not authorise.",
      },
      {
        clause: "Advisors employed by a retailer or distributor",
        why: "Their own employer usually restricts this, and the conflict is a real problem for both sides.",
      },
    ],
    cpgIssues: [
      "Category advisors commonly hold positions with several brands in the same set, which makes confidentiality over landed cost, promotional calendars and velocity a live commercial issue.",
      "Equity for a single co-packer or distributor introduction should be sized and vested against that introduction, not paid as an open-ended stake.",
      "An advisor who is a current buyer, category manager or distributor executive may be restricted by their employer, and that needs to be addressed rather than assumed.",
      "Advisors sometimes want equity instead of a broker commission, which changes the relationship, the disclosure to future investors, and how the person is treated at exit.",
      "Every advisor on the cap table becomes a signature you need at a financing or a brand sale, so a small grant carries an administrative cost forever.",
    ],
    founderMistakes: [
      "Giving two percent, unvested, for an introduction to a co-packer, and carrying it through two financings.",
      "Never writing down what the advisor was supposed to do, so there was no basis to end the arrangement.",
      "Sharing cost sheets and account performance with an advisor who was advising a direct competitor.",
      "Letting an advisor negotiate with a distributor and finding out terms had been discussed that the company had not approved.",
    ],
    negotiationPoints: [
      "Ask for a written scope: named accounts, categories or hours per month, and a term with an end date.",
      "Ask for monthly vesting with a short cliff, so the grant tracks actual engagement.",
      "Ask for a conflicts provision requiring disclosure of other brands in your category.",
      "Ask that unvested equity is forfeited on termination, and that either side can terminate with short notice.",
      "Ask counsel how to structure the grant before you agree a percentage in conversation.",
    ],
    redFlags: [
      "Equity granted outright with no vesting for an introduction.",
      "No term, so the arrangement runs indefinitely.",
      "An advisor unwilling to disclose other brands they advise in your category.",
      "A percentage agreed verbally before anyone has defined the work.",
      "An advisor asking for authority to negotiate on your behalf.",
    ],
    beforeYouSign: [
      "A written list of what this advisor will do in the next six months, specifically.",
      "Their other current advisory roles and any employer restrictions.",
      "A cap table showing what the grant costs you fully diluted.",
    ],
    related: [
      "advisory-board-agreement",
      "advisor-agreement",
      "equity-incentive-plan",
      "vesting-agreement",
      "advisory-equity-agreement",
      "cpg-broker-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Granting equity to a non-employee is a securities transaction with tax consequences for both sides, and some elections tied to the grant date run on short deadlines that are easy to miss — take counsel and tax advice before agreeing a number.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "advisory-board-agreement",
    name: "Advisory Board Agreement",
    category: "formation",
    subcategory: "Advisors & directors",
    aliases: ["Board of Advisors Agreement", "Advisory Council Agreement"],
    stages: ["pre-launch", "growth", "fundraising"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Advisory Board Member",
    whenYouNeedIt:
      "When you want a standing group of category people meeting on a schedule to help with retail strategy, operations or fundraising — as distinct from appointing anyone to the actual board.",
    whatItDoes:
      "Sets the terms for serving on an advisory body that advises but does not govern: meeting cadence, expectations, compensation, confidentiality, IP and conflicts.",
    whyItMatters:
      "An advisory board is one of the cheapest ways for a small brand to get access to people who have run national accounts and survived their first recall. It is also where founders most often blur a line: an advisory board advises, it does not govern, and describing advisors as board members in a deck becomes awkward when an investor asks for board minutes that do not exist. Getting confidentiality right matters too, because these meetings are where your cost structure, velocity and account plans get discussed openly.",
    majorTerms: [
      "Role, and explicit statement that it is advisory only",
      "Meeting cadence and expected time commitment",
      "Term and renewal",
      "Compensation: cash, equity or expenses",
      "Vesting on any equity granted",
      "Confidentiality obligations",
      "Assignment of anything created for the company",
      "Conflicts and disclosure of competing engagements",
      "Termination and removal",
      "No authority to bind or represent the company",
    ],
    clausesToWatch: [
      {
        clause: "Advisory only, no governance authority",
        why: "Leaving this unsaid encourages the assumption that advisors have decision rights they do not have.",
      },
      {
        clause: "Confidentiality scope",
        why: "Advisory meetings cover cost, margin and account plans, which is exactly the information you cannot afford to have travel.",
      },
      {
        clause: "Conflicts disclosure",
        why: "Category advisors often serve several brands, and undisclosed overlap turns a helpful meeting into a leak.",
      },
      {
        clause: "Term and removal",
        why: "Without a term, an advisory board becomes a list of names nobody can remove.",
      },
      {
        clause: "Compensation and vesting",
        why: "Equity that does not vest against actual attendance rewards the announcement rather than the work.",
      },
    ],
    cpgIssues: [
      "Useful advisors in this industry are usually still active — a buyer, a distributor executive, a co-packer operator — so their employer's restrictions and their conflicts need to be dealt with up front.",
      "Advisory sessions typically review landed cost, promotional performance and account-level velocity, which is competitively sensitive in a small category.",
      "An advisor who also advises a competing brand may sit in the same retailer category review on the other side.",
      "Founders sometimes list advisors as board members in investor decks, which becomes a problem when governance documents are requested.",
      "Advisory boards are often assembled to help with a specific step — a chain presentation, a co-packer transition, a fundraise — and should have a term that matches that purpose.",
    ],
    founderMistakes: [
      "Assembling an advisory board for the deck and never holding a meeting, then being asked about it by investors.",
      "Granting equity to five advisors with no vesting and no term.",
      "Sharing full cost and velocity data with an advisor who was engaged with a direct competitor.",
      "Describing advisors as directors in materials while the actual board consisted of two founders.",
    ],
    negotiationPoints: [
      "Ask for a defined term with renewal by agreement rather than an open-ended appointment.",
      "Ask for conflicts disclosure covering all brands in your category, updated annually.",
      "Ask that any equity vests against attendance and the agreed cadence.",
      "Ask for confidentiality that expressly covers formulations, cost and account performance data.",
    ],
    redFlags: [
      "No confidentiality obligation at all.",
      "An advisor unwilling to disclose competing engagements.",
      "Equity granted with no vesting and no term.",
      "Advisory members described publicly as directors.",
    ],
    beforeYouSign: [
      "A written purpose for the advisory board and what it is expected to produce.",
      "Each member's current roles and category conflicts.",
    ],
    related: [
      "advisor-equity-agreement",
      "advisor-agreement",
      "independent-director-agreement",
      "mutual-nda",
      "confidentiality-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "independent-director-agreement",
    name: "Independent Director Agreement",
    category: "formation",
    subcategory: "Advisors & directors",
    aliases: ["Outside Director Agreement", "Board Member Agreement", "Non-Executive Director Agreement"],
    stages: ["fundraising", "growth", "exit"],
    importance: "advanced",
    entityTypes: ["corporation"],
    parties: "Corporation + Independent Director",
    whenYouNeedIt:
      "When you add someone to the actual board who is neither a founder nor an investor designee, usually as part of a financing or when the board needs a tiebreak.",
    whatItDoes:
      "Sets the terms of board service: expectations and time commitment, compensation, term, confidentiality, conflicts, and the indemnification and insurance the director will require.",
    whyItMatters:
      "An independent director on a small CPG board is usually a category veteran who has done what you are about to do — a national launch, a co-packer transition, a recall. They are also the person who often holds the deciding vote on equity grants, debt and a sale, which is why the terms of their appointment deserve more thought than founders give them. Experienced directors will not join without indemnification and insurance, so this document is usually the moment a company first confronts both.",
    majorTerms: [
      "Board seat, term and re-election",
      "Time commitment and meeting attendance expectations",
      "Compensation, usually equity, and its vesting",
      "Expense reimbursement",
      "Indemnification and advancement of expenses",
      "Directors and officers insurance",
      "Confidentiality obligations",
      "Conflicts of interest and disclosure",
      "Resignation and removal",
      "Committee service, if any",
    ],
    clausesToWatch: [
      {
        clause: "Indemnification and expense advancement",
        why: "Without advancement, a director pays their own defence costs while a dispute runs, and experienced directors will not accept that.",
      },
      {
        clause: "Directors and officers insurance",
        why: "Most early CPG companies do not carry it, and the first director you recruit will make it a condition.",
      },
      {
        clause: "Conflicts and other board seats",
        why: "A category veteran may serve several food and beverage boards, creating overlap on co-packers, brokers and ingredient supply.",
      },
      {
        clause: "Equity compensation and vesting",
        why: "A large grant with no vesting rewards joining rather than serving, and it is hard to unwind if attendance lapses.",
      },
      {
        clause: "Removal and resignation",
        why: "Without a clean mechanism, a director who has disengaged stays on the board through your next financing.",
      },
    ],
    cpgIssues: [
      "The most useful independent directors in food and beverage are still active in the industry, which means genuine conflicts around shared co-packer capacity, broker houses and ingredient suppliers.",
      "They will see cost structure, promotional spend and account-level performance, which is competitively sensitive in a small category.",
      "Directors approve the commitments that matter operationally: co-packer minimums, equipment finance, personal guarantees and equity grants.",
      "Directors and officers insurance is a real cost that early CPG companies rarely carry until an outside director asks for it, and it is separate from your product liability cover.",
      "An executive currently employed by a retailer or distributor may be restricted from serving, and that has to be checked rather than assumed.",
    ],
    founderMistakes: [
      "Recruiting an outside director with a large unvested equity grant who then attended two meetings.",
      "Offering a seat with no indemnification agreement and no insurance, and losing the candidate.",
      "Not asking about other board seats and finding the director also served a direct competitor.",
      "Having no removal mechanism when the relationship stopped working.",
    ],
    negotiationPoints: [
      "Ask for vesting on the director's equity tied to continued service and attendance.",
      "Ask for a written conflicts disclosure covering every other board seat and advisory role in the category.",
      "Ask what insurance the company needs to put in place, and price it before you make the offer.",
      "Ask for a defined term with re-election rather than an indefinite seat.",
    ],
    redFlags: [
      "A large equity grant with no vesting or service condition.",
      "A candidate who declines to disclose other board positions.",
      "No indemnification agreement offered alongside the appointment.",
      "No mechanism to remove or replace the director.",
    ],
    beforeYouSign: [
      "The bylaws and any voting or stockholders agreement governing board composition.",
      "A quote for directors and officers insurance.",
      "The candidate's other board seats and industry relationships.",
    ],
    related: [
      "indemnification-agreement",
      "board-consent",
      "advisory-board-agreement",
      "corporate-bylaws",
      "voting-agreement",
      "board-observer-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "indemnification-agreement",
    name: "Indemnification Agreement",
    category: "formation",
    subcategory: "Advisors & directors",
    aliases: ["Director Indemnification Agreement", "Officer Indemnification Agreement", "D&O Indemnity Agreement"],
    stages: ["fundraising", "growth", "exit"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Director or Officer",
    whenYouNeedIt:
      "When you appoint officers, when you recruit an outside director or investor designee, and generally around your first financing.",
    whatItDoes:
      "Commits the company to indemnify a director or officer for claims arising from their service and, importantly, to advance their defence costs while a matter is ongoing rather than reimbursing at the end.",
    whyItMatters:
      "Consumer brands attract claims that name individuals: disputes over label claims and net contents, competitor challenges to advertising, a distributor or co-packer fight that pulls in whoever signed. Whether a person stays in a case is for counsel to sort out, but the defence costs start on day one and someone has to fund them. Without an indemnification agreement, that someone can be the individual, which is why experienced directors and officers treat this as a condition of serving rather than a nicety.",
    majorTerms: [
      "Scope of indemnification and who is covered",
      "Advancement of expenses during a proceeding",
      "Undertaking to repay if indemnification turns out not to apply",
      "Exclusions from coverage",
      "Relationship to directors and officers insurance",
      "Procedure for making a claim and selecting counsel",
      "Survival after the person leaves the company",
      "Priority between the company and any investor indemnitor",
      "Subrogation and contribution",
    ],
    clausesToWatch: [
      {
        clause: "Advancement of expenses",
        why: "Reimbursement at the end of a matter is not much use to an individual funding a defence for a year in the meantime.",
      },
      {
        clause: "Exclusions",
        why: "Broad exclusions can hollow the protection out precisely in the situations where it would matter.",
      },
      {
        clause: "Survival after departure",
        why: "Claims often arrive after someone has left, so protection that ends with the role protects nobody.",
      },
      {
        clause: "Insurance obligations",
        why: "An agreement to maintain insurance is only as good as the policy, and many early companies never buy one.",
      },
      {
        clause: "Priority with investor indemnitors",
        why: "Where an investor also indemnifies its designee, the order of payment needs to be stated or it becomes a fight between them.",
      },
    ],
    cpgIssues: [
      "Consumer-facing claims about labelling, ingredient statements, net contents and advertising are a normal part of this industry, and individuals sometimes get named alongside the company.",
      "Recalls and withdrawals generate parallel disputes with co-packers, distributors and retailers, and the officers who signed those contracts are drawn into them.",
      "Product liability insurance covers the product; it does not cover a director's defence in a governance or shareholder dispute, so the two protections are separate.",
      "Founders serving as officers of a brand often sign co-packer, distribution and retailer agreements personally as signatory, which is how they come to be named.",
      "An outside director with retail or distribution experience will ask for this document and for insurance before joining, so budget for both when you plan the board.",
    ],
    founderMistakes: [
      "Assuming the bylaws alone gave officers protection, without ever signing individual agreements.",
      "Offering a board seat without indemnification and losing the candidate.",
      "Believing product liability cover extended to a governance dispute.",
      "Signing agreements with no protection in place and discovering the gap when a distributor dispute named the founder personally.",
    ],
    negotiationPoints: [
      "Ask for expense advancement, not just reimbursement after the fact.",
      "Ask that the protection survives departure and covers acts during the period of service.",
      "Ask for an obligation to maintain insurance at a stated level, and confirm the policy actually exists.",
      "Ask that exclusions are narrow and specific rather than broad categories.",
      "Ask counsel to align the agreement with the bylaws so the two do not contradict each other.",
    ],
    redFlags: [
      "No advancement of expenses.",
      "Protection that ends when the person leaves.",
      "An insurance commitment with no policy behind it.",
      "Exclusions broad enough to cover most realistic claims.",
    ],
    beforeYouSign: [
      "The bylaws provisions on indemnification, to check they are consistent.",
      "The current directors and officers policy, or a quote if none exists.",
      "A list of who currently signs agreements on the company's behalf.",
    ],
    related: [
      "corporate-bylaws",
      "independent-director-agreement",
      "board-consent",
      "officer-appointment-resolution",
      "recall-responsibility-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default FORMATION_DOCS;
